import React, { useEffect, useMemo, useRef, useState } from "react";
import { RESUME_HELP, RESUME_ACCEPT } from "@/lib/resumeHelp";
import { isInAppBrowser } from "../utils/browserEnv";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import { ArrowLeft, Lock, Upload } from "lucide-react";
import { apiUrl, preconnectApi } from "@/lib/api";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { storeAuthTokens, resolvePostLoginPath } from "@/utils/postAuth";
import { getAttributionPayload, metaTrack, metaTrackCustom, gaTrack, signupAudience } from "@/lib/analytics";
import { trackEvent, getAnonymousId } from "@/lib/tracker";
import { pendingPostReturn } from "@/lib/postDrafts";
import { getVariant, peekVariant, INDIVIDUAL_START_EXPERIMENT, INDIVIDUAL_START_ARMS, COMPANY_FLOW_EXPERIMENT } from "@/lib/experiment";
import LocationInput from "@/components/LocationInput";
import StepProgress from "@/components/StepProgress";
import { useBranding } from "@/contexts/BrandingContext";
import {
  stashPendingDraft,
  saveSignupProgress,
  loadSignupProgress,
  clearSignupProgress,
  hasSeenPeek,
  markPeekSeen,
  PEEK_ENABLED,
} from "@/lib/needDraft";
import { startResumeIntake, quickReadResume, withinMs, clearResumeIntake } from "@/lib/resumeIntake";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import PeerPeek from "@/components/talent/PeerPeek";
import ResumePostPreview from "@/components/talent/ResumePostPreview";
import { SiftMark } from "@/components/SiftWordmark";
import { useFieldErrors, FieldError } from "@/lib/formErrors";
import { nameFromEmail } from "@/lib/nameFromEmail";
import { useAutofillAdoption } from "@/lib/autofillAdoption";
import useDialog from "@/hooks/useDialog";
import CompanyDashboardPreview from "@/components/preview/CompanyDashboardPreview";
import ContractorDashboardPreview from "@/components/preview/ContractorDashboardPreview";

// Post-first signup, both sides of the marketplace. The landing CTAs say
// "Post what you need" and "Post your availability", so that is literally the
// first question, not "create an account". We fill the posting in for them,
// let them edit it, collect the practical details (where, when, how long,
// money), and only then ask who they are. The account is the last step and it
// is framed as what saves the work they already did, which is a very different
// ask from "sign up to see anything".
//
// The old flow (GetStartedProgressive) asked name -> email -> company and
// dropped them into an empty composer. That is what produced signups with no
// postings: we took the details first and gave value second.
//
// One component, two audiences, because the machinery is identical: same
// stages, same parked-draft recovery, same account-then-save ordering. Only
// the words, the register endpoint, the poster_type, and which dashboard shell
// gets revealed differ, and those live in COPY below. What is NOT shared is
// the drafting prompt: the backend keeps separate ones, because writing a
// business's need in a worker's first-person voice produces a job ad where an
// availability post belongs.
//
// Individuals get a third on-ramp: upload a resume and get THREE candidate
// availability posts plus a filled-in profile. A resume already contains
// everything this flow asks for, and most people can be hired more than one
// way, so we show all three and let them publish any or all of them.
//
// The drafting calls are deliberately invisible. No "AI wrote this" chrome, no
// sparkles, no accept/reject moment: the fields are simply populated when the
// screen arrives, and the user edits them like any other form. If drafting is
// off (no ANTHROPIC_API_KEY) or fails, what they typed becomes the title and
// the description starts empty. Same screen either way, so nothing about the
// flow advertises whether the help fired.
//
// ?a=business / ?a=talent come from CTAs that already declared the audience,
// and the flow now trusts them: everyone starts on the post. There used to be
// a confirmation screen in front of this asking "are you posting opportunities
// or looking for them", which is the question the CTA had just answered — 8 of
// the first 20 visitors tapped neither card and left. The escape hatch for
// people who came through the wrong door is a single line under the composer's
// primary button, and switching preserves whatever they already typed.

const COPY = {
  business: {
    accountType: "organization",
    posterType: "org",
    registerPath: "/api/accounts/register_org_passwordless/",
    postingsPath: "/company/postings",
    startTitle: "Hiring for something?",
    startSub: "Tell us the job title and we'll write the post for you. You can edit it before anything goes live, or skip it and set up your account.",
    positionPlaceholder: "e.g. CDL driver, Line cook, Front desk",
    descPlaceholder: "What the work involves, where it happens, and the schedule.",
    titlePlaceholder: "Night-shift CDL driver, local routes",
    whereLabel: "Where",
    wherePlaceholder: "Carmel, IN, or Remote",
    whenLabel: "When do you need someone?",
    durationLabel: "How long",
    moneyLabel: "Budget",
    moneyPlaceholder: "$28/hr, or $3,000 for the job",
    moneyHint: "Shown on your post. Naming a number gets more of the right people to respond.",
    moneyPrefix: "Budget",
    readyBody: "Add your company details to save it and unlock this dashboard.",
    accountBody: "This saves your post and opens your dashboard.",
    /* "Work email" read as a commitment people were not ready to make. */
    emailLabel: "Email",
    emailPlaceholder: "you@company.com",
    /* Company posts are saved as drafts now (see createAccountAndPost), so
       neither of these may claim the post is live or being matched. */
    savedPending: "It's saved as a draft. Publish it from your home when you're ready.",
    savedLive: "It's saved as a draft. Publish it from your home when you're ready.",
    // No-post signup. Post-first exists because signups that never posted were
    // the failure mode, but *requiring* the post turned "I just want to look
    // around first" into a dead end with no account at the end of it. Both
    // doors now. It's the same account either way, so someone who skips can
    // post ten minutes later from the dashboard.
    accountBodySkip: "This opens your dashboard.",
    savedSkipBody: "Nothing is posted yet, and that's fine. Have a look at who's available, and post when you're ready.",
    browsePath: "/browse?show=person",
    browseLabel: "See who's available",
  },
  talent: {
    accountType: "individual",
    posterType: "contractor",
    registerPath: "/api/accounts/register_passwordless/",
    postingsPath: "/dashboard/contractor/postings",
    startTitle: "Be your own boss. Control your schedule.",
    // Says what the upload BUYS them, not just that it exists. The old line
    // ("Upload your resume, or type what you're interested in doing") described
    // a file operation and read as a chore — of the visitors who tapped upload,
    // most never picked a file. A resume is the single highest-value thing an
    // individual can hand us, so the payoff belongs in the first sentence.
    /* "You can add it later" is load-bearing, not a throwaway. This traffic is
       96% mobile and mostly inside Facebook's in-app browser, where the file
       picker opens on Photos: people shoot the page or hunt for a file they
       don't have on their phone, and 4 of the 9 who tried a resume draft hit
       image_file_unreadable or upload_failed. Saying the upload is optional and
       repeatable turns that dead end into a deferral instead of an exit. */
    startSub: "",
    descPlaceholder: "What you do, how you work, and anything a business should know.",
    titlePlaceholder: "Available residential electrician, evenings and weekends",
    whereLabel: "Where do you work?",
    wherePlaceholder: "Carmel, IN, or Remote",
    whenLabel: "When are you available from?",
    durationLabel: "How long are you available",
    moneyLabel: "Your rate",
    moneyPlaceholder: "$45/hr, or $500 per job",
    moneyHint: "Shown on your post. Naming a number gets more of the right businesses to reach out.",
    moneyPrefix: "Rate",
    readyBody: "Add your details to save it and unlock this dashboard.",
    /* What finishing BUYS them, in their own terms.

       The predecessor was "This saves your post and opens your dashboard. No
       card, no password to pick right now." Three claims, two of which
       answered questions nobody had asked. "No card" belongs on a checkout
       page — raising it on a free signup is the first time the reader has
       thought about a card at all. And "no password to pick" was offered as a
       kindness while describing the exact thing that left people with no way
       back into the account they had just made. */
    accountBody: "Finish creating your account to unlock your pre-filled profile and get your posts.",
    emailLabel: "Email",
    emailPlaceholder: "you@email.com",
    savedPending: "First posts get a quick review, then they go live and businesses start seeing them.",
    savedLive: "We're matching against businesses hiring right now.",
    accountBodySkip: "Set a password and you're in. Add your resume on the next screen and we'll fill in your profile and write your posts from it.",
    savedSkipBody: "Nothing is posted yet, and that's fine. Have a look at the open work, and post your availability when you're ready.",
    browsePath: "/browse?show=job",
    browseLabel: "Browse open work",
  },
};

const DURATIONS = [
  { value: "one_time", label: "One-time / single job", days: 0 },
  { value: "lt_month", label: "Less than a month", days: 30 },
  { value: "1_3", label: "1 to 3 months", days: 90 },
  { value: "3_6", label: "3 to 6 months", days: 180 },
  { value: "ongoing", label: "Ongoing / no end date", days: null },
];

const FALLBACK_TYPES = [
  { value: "general", label: "General / Other" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "staffing_agency", label: "Staffing Agency" },
  { value: "trades_services", label: "Trades & Services" },
  { value: "home_services", label: "Home Services" },
  { value: "professional_services", label: "Professional Services" },
  { value: "healthcare", label: "Healthcare" },
  { value: "hospitality", label: "Hospitality / Events" },
  { value: "transportation", label: "Transportation / Trucking" },
];

// What to say when a resume can't be read. A dead end ("we couldn't read
// that") loses the person; naming the specific problem and the specific fix
// keeps them going, and for most of these the fix is 30 seconds of work in the
// app they already have the file open in.

// Every drafting call is a billed model request on an endpoint that needs no
// account, so it carries a per-visitor budget. Hitting it is not a failure of
// the file or of what they typed — say so plainly and point at the way forward,
// which is that they can always write the post themselves.
const PICKER_FLAG = "sift_resume_picker_open";

const QUOTA_REASONS = new Set(["draft_limit_visitor", "draft_limit_ip"]);
const QUOTA_HELP =
  "You've used your drafts for today. You can still write your post yourself below, it works exactly the same.";

/* A photo that came out unreadable is the one failure worth coaching, because
   it is the only one where the person already has everything they need and
   just has to take the picture again. The format failures in RESUME_HELP are
   already instructions ("open it, choose Save As, pick PDF"); repeating them
   as tips would be noise. */
const RESUME_RETRY_TIPS = {
  image_file_unreadable: [
    "Lay the page flat and fill the frame with it.",
    "Bright, even light. Watch for shadow from your hand or the phone.",
    "If you have the PDF or Word version anywhere, that always reads better.",
  ],
  scanned_pdf_unreadable: [
    "A photo taken in good light often reads better than an old scan.",
    "If you have the original Word or PDF, that is the surest one.",
  ],
};

// The file was read fine, there just wasn't enough in it to write from.
// Re-uploading the same thing cannot change that, so don't offer a retry that
// is guaranteed to fail; carrying on without it is the honest way forward.
const RESUME_RETRY_WONT_HELP = new Set(["too_little_text", "could_not_draft"]);

const blankDraft = () => ({
  title: "",
  description: "",
  vertical: "",
  offering: "",
  location: "",
  selected: true,
});

const validEmail = (v) => /\S+@\S+\.\S+/.test(v || "");

const addDays = (iso, days) => {
  if (!iso || days == null) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/* `ask`, `startAt`, `onBackToAsk`: set by CompanyAsk, where a company has
   already said what it needs. This page is then the new arm of
   company_signup_flow: it drafts from their words and goes straight to the
   1-of-2 card (ReadyReveal), or, for an answer that isn't a hire, straight to
   the account (2 of 2). */
export default function GetStartedNeedFirst({ audience = "business", onSwitchAudience, ask = null, startAt = null, onBackToAsk = null }) {
  const navigate = useNavigate();
  const branding = useBranding();
  const [params] = useSearchParams();

  const talent = audience === "talent";
  const c = COPY[talent ? "talent" : "business"];

  // Work in flight from a previous visit that ended in a reload. Read once, at
  // first render, so every field below can seed itself from it — a restore
  // effect would have to fight the save effect for who writes last.
  // Coming from CompanyAsk is a fresh start: their answer, not old progress.
  const [restored] = useState(() => (ask ? null : loadSignupProgress(audience)));

  // Whether the worked-examples screen has been shown and dismissed. Seeded
  // from sessionStorage, not just component state: a remount (this webview
  // reloads freely) or an audience switch would otherwise re-show it, and
  // production found both within a day. Also true for a restored draft, so
  // someone resuming lands straight back on their own work.
  const [peeked, setPeeked] = useState(
    () => hasSeenPeek() || !!loadSignupProgress(audience)
  );

  /* Stages.

     Individuals:  start -> [resume] -> draft -> details -> ready -> account -> saved
                   ...or start -> account -> saved when they don't upload one.
     Businesses:   account -> saved.

     The first screen used to be a free-text composer asking what you needed
     done, and it was where the funnel died. Of 22 people who reached it in a
     day, 13 produced no interaction at all — they read it and left — and not
     one person who got past it had done so by typing: every single one used
     the resume upload. One visitor on an old iPhone tapped Continue 31 times
     in 60 seconds against an empty box before giving up.

     The lesson was about the size of the question, not about asking one. "What
     do you need done?" over a four-row textarea is an essay prompt, and people
     either write nothing or leave. A job title is two words, and the reader
     already knows the answer before the page finishes loading. So each side
     now gets one small, concrete ask with the account sitting beside it as an
     equal option, never behind it:

       individuals — a resume, which drafts three posts and a profile
       businesses  — a job title, which drafts the post they can then edit

     Either way, skipping is a button and not a buried link, and an empty
     submit moves them on rather than scolding them.

     "saved" is terminal and means the account already exists, so it is never
     restored into: that screen's state lives on the server now. */
  const [stage, setStage] = useState(
    startAt === "account" ? "account"
      : restored?.stage && restored.stage !== "saved" ? restored.stage : "start"
  );

  /* Nothing will be posted; the account is the whole deliverable. Set when
     they choose the account over the offer on the start screen. It makes the
     save loop a no-op rather than being inferred from an empty `selected`, so
     a genuine "nothing selected" bug still trips the guard in
     createAccountAndPost instead of silently creating a postless account. */
  const [skipPost, setSkipPost] = useState(startAt === "account" ? true : (restored?.skipPost ?? false));
  const [drafting, setDrafting] = useState(Boolean(ask && !startAt));

  /* Did the file picker take the whole webview with it?

     Inside Facebook's and Instagram's in-app browsers, opening a file picker
     can suspend the page and bring it back reloaded, with the file never
     arriving. It is not a hypothetical: over fourteen days, 90% of people on a
     real browser who tapped upload reached /welcome, against 53% in Facebook's
     browser and 45% in Instagram's -- about 80 people.

     A flag is written the moment the picker is opened and cleared when a file
     actually arrives. Finding it still set on mount means the round trip failed,
     so we say so and offer the path that does not need a file. */
  const [pickerLost, setPickerLost] = useState(false);
  const inApp = useMemo(() => { try { return isInAppBrowser(); } catch { return false; } }, []);
  useEffect(() => {
    try {
      const at = Number(window.sessionStorage.getItem(PICKER_FLAG) || 0);
      window.sessionStorage.removeItem(PICKER_FLAG);
      // Recent only. A stale flag from an hour ago is not this trip.
      if (at && Date.now() - at < 5 * 60 * 1000) {
        setPickerLost(true);
        trackEvent("resume_picker_lost", { in_app: inApp, account_type: c.accountType });
      }
    } catch { /* private mode */ }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Businesses: the job title we draft the posting from. One line, not a
  // textarea — the shape of the input is the whole difference between "give me
  // two words" and "write me a paragraph".
  const [positionTitle, setPositionTitle] = useState(() => {
    if (ask?.text) return ask.text.slice(0, 300);
    if (restored?.positionTitle) return restored.positionTitle;
    /* An individual who wrote their answer on the company side ("I'm looking
       for work") arrives with it carried over. */
    if (audience === "talent") {
      try {
        const v = window.sessionStorage.getItem("sift_talent_need") || "";
        if (v) window.sessionStorage.removeItem("sift_talent_need");
        if (v) return v.slice(0, 300);
      } catch { /* storage blocked */ }
    }
    /* A company that typed its need in the landing page's search box arrives
       with it already written, as CompanyDoors does on the other arm. */
    if (audience !== "talent") {
      try {
        const v = window.sessionStorage.getItem("sift_hero_need") || "";
        if (v) window.sessionStorage.removeItem("sift_hero_need");
        return v.slice(0, 120);
      } catch { /* storage blocked */ }
    }
    return "";
  });

  // Always an array. The resume path fills it with three, any of which the
  // person can publish. One shape means the details step, the reveal, and the
  // save loop never branch on count.
  const [drafts, setDrafts] = useState(() =>
    restored?.drafts?.length ? restored.drafts : [blankDraft()]
  );

  // Resume path (individuals only): the file itself, kept so it can be
  // uploaded for real once there is an account to attach it to. A File is the
  // one thing a restore cannot bring back — it never existed anywhere but
  // memory — so a signup recovered after a reload simply asks for it again on
  // the finish screen. What the file was being read FOR lives in
  // lib/resumeIntake, which outlives this component on purpose.
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeNote, setResumeNote] = useState("");
  // Why a failed read is more than a message: see ResumeFailure.
  const [resumeFailure, setResumeFailure] = useState(null); // { reason, help, tips, salvaged }
  const fileInputRef = useRef(null);

  const [location, setLocation] = useState(restored?.location || "");
  const [startDate, setStartDate] = useState(restored?.startDate || "");
  const [duration, setDuration] = useState(restored?.duration || "lt_month");
  const [budget, setBudget] = useState(restored?.budget || "");

  const [name, setName] = useState(restored?.name || "");
  const [email, setEmail] = useState(restored?.email || "");
  /* Never restored and never saved. Everything else in this flow survives a
     reload through localStorage; a password must not, so someone who abandons
     signup on a shared phone doesn't leave one sitting on the device. */
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState(restored?.orgName || "");
  const [bizType, setBizType] = useState(restored?.bizType || "general");

  // Referral code. Seeded from the partner's link when there is one, otherwise
  // typed on the account step. Restored state wins over the URL so a reload
  // mid-signup doesn't wipe a code they typed by hand.
  const [partnerCode, setPartnerCode] = useState(
    restored?.partnerCode ?? (params.get("partner_code") || params.get("ref") || params.get("referral_code") || "").trim()
  );
  // Kept out of the way unless it's relevant. A code that arrived on the link
  // shows so the business can see the referral registered; everyone else gets
  // one quiet line rather than another field to skip past.
  const [showPartnerCode, setShowPartnerCode] = useState(!!partnerCode);
  const [bizTypes, setBizTypes] = useState(FALLBACK_TYPES);
  const [submitting, setSubmitting] = useState(false);

  // Inline, on-the-field validation errors. A toast is invisible in the
  // in-app browser this funnel mostly runs in; see lib/formErrors.
  const {
    errors,
    fieldProps,
    fail: failValidation,
    showFieldError,
    clear: clearError,
    reset: resetErrors,
    readField,
    readFieldRaw,
    refs: fieldRefs,
  } = useFieldErrors({ account_type: c.accountType, flow: "need_first" });

  /* Keep what iOS AutoFill puts in these boxes.

     Controlled inputs let React write its own state back over an autofilled
     value on the next commit, so the person is looking at their email address
     while we insist they enter one. Two of the most recent blocks are exactly
     that: an iPhone, `email`, `value_length: 0`, two taps nine seconds apart.
     Re-bound per stage because the fields on screen change with it. */
  const adoptAutofill = useAutofillAdoption(
    fieldRefs,
    { name: setName, email: setEmail, password: setPassword, orgName: setOrgName },
    [stage],
  );

  // Success screen: how many posts landed and whether they're in moderation,
  // plus the inline password step. The account is passwordless, so this is
  // what lets them come back on another device without the claim email.
  const [savedCount, setSavedCount] = useState(1);
  const [pending, setPending] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwdSet, setPwdSet] = useState(false);
  const [settingPwd, setSettingPwd] = useState(false);

  // The confirm screen was a measurement blind spot. Switching sides fired
  // get_started_choose, but answering with the side we had already assumed
  // The step that matters now that the confirm screen is gone: did they get to
  // a composer they could actually type into? Everything before this is the
  // CTA doing its job; everything after is them doing work.
  /* Which first screen an individual sees (lib/experiment.js). Assigned only
     on the talent side, and before the view event below fires, so that event
     and everything after it carries exp_individual_start. */
  const startArm = useMemo(
    () => (talent ? getVariant(INDIVIDUAL_START_EXPERIMENT, INDIVIDUAL_START_ARMS, { primary: false }) : null),
    [talent],
  );
  const oldStart = startArm === "resume_first";
  /* For a company this page is the `new` arm of company_signup_flow (the
     mockup flow; GetStartedPreview routes the `current` arm to CompanyDoors).
     Its steps are tagged so the two flows are scored the same way. */
  useEffect(() => {
    if (talent) return;
    const arm = peekVariant(COMPANY_FLOW_EXPERIMENT) || "new";
    if (stage === "start") trackEvent("company_flow_shown", { arm, door: "need_first" });
    if (stage === "account") trackEvent("company_flow_account", { arm, door: "need_first" });
  }, [stage, talent]);
  useEffect(() => {
    if (stage !== "start") return;
    trackEvent("need_composer_view", {
      account_type: c.accountType,
      flow: "need_first",
      audience,
      ...(startArm ? { start_arm: startArm } : {}),
    });
  }, [stage, c.accountType, audience, startArm]);

  // The escape hatch that replaced the confirm screen. Switching re-renders
  // this component with the other side's copy but does NOT remount it, so
  // whatever they already typed survives the switch — which is the point:
  // someone who describes their work and only then realizes they came through
  // the wrong door shouldn't have to type it again.
  const switchSide = () => {
    const other = talent ? "business" : "talent";
    if (onSwitchAudience) return onSwitchAudience(other);
    return navigate(`/get-started?a=${other}`);
  };

  // get_started_preview_view is the funnel's ENTRY marker, not just a GA event:
  // the dashboard scopes "Signed up" to visitors who fired one of the entry
  // names, so a landing CTA arriving at ?a=business (which skips the chooser)
  // has to fire it here or its signups get filtered out of the funnel entirely.
  useEffect(() => {
    gaTrack("get_started_preview_view", { account_type: c.accountType });
    trackEvent("get_started_preview_view", { audience, account_type: c.accountType, flow: "need_first" });
    trackEvent("signup_started", { account_type: c.accountType, flow: "need_first" });
  }, [c.accountType, audience]);

  /* Open the connection to the API before they pick a file. The resume read
     is two uploads the moment a file arrives, and the first is on the clock:
     the signup screen shows its post as soon as it lands. Both sides draft
     from this screen, so both get it. */
  useEffect(() => preconnectApi(), []);

  // Live business-type catalog for the account step (falls back to the static
  // list). Business only: an individual has no business type to pick.
  useEffect(() => {
    if (talent) return undefined;
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/accounts/business-types/"));
        const data = await res.json();
        if (!ignore && Array.isArray(data?.results) && data.results.length) setBizTypes(data.results);
      } catch {
        /* keep fallback */
      }
    })();
    return () => { ignore = true; };
  }, [talent]);

  // Hold everything they've entered against a reload we didn't ask for. The
  // reload this exists for arrives without warning, so there is no later
  // moment to save at — it has to be continuous.
  //
  // Debounced, because serialising a multi-paragraph draft on every keystroke
  // is a synchronous localStorage write on the exact slow phones this is meant
  // to help, and typing lag would cost more people than the crash does. A
  // pause of a third of a second is well inside the gap between the last
  // keystroke and the tap that moves them on, so nothing real is at risk.
  //
  // Stops once the account exists: from then on the work is on the server and
  // all that's left in here is their email address.
  useEffect(() => {
    if (stage === "saved") return undefined;
    const id = setTimeout(() => {
      saveSignupProgress({
        audience, stage, skipPost, positionTitle, drafts,
        location, startDate, duration, budget,
        name, email, orgName, bizType, partnerCode,
      });
    }, 300);
    return () => clearTimeout(id);
  }, [audience, stage, skipPost, positionTitle, drafts,
      location, startDate, duration, budget, name, email, orgName, bizType, partnerCode]);

  // Tell them their work survived, and count it. Without this event a recovery
  // is invisible in the funnel and looks identical to the abandonment it just
  // prevented, so there'd be no way to know whether any of this is working.
  useEffect(() => {
    if (!restored) return;
    trackEvent("get_started_restored", {
      account_type: c.accountType, flow: "need_first", audience, stage: restored.stage || "need",
    });
    toast("Picked up where you left off.", { icon: "↩️" });
  }, [restored, c.accountType, audience]);

  const firstName = name.trim().split(/\s+/)[0] || "there";
  const selected = drafts.filter((d) => d.selected && d.title.trim());
  const multi = drafts.length > 1;

  /* A resume is being read for them somewhere behind this screen. True from
     the moment they pick a file until the finish-setup screen collects the
     result, and it is what turns the account form from a checkpoint into the
     last step of something already underway. */
  const resumeIncoming = talent && !!resumeFile;

  /* Enough in the boxes to be worth submitting. Email and a password are the
     only things registration genuinely needs on this side; the name is derived
     from the address when it is blank, so requiring it here would grey the
     button over a field we do not need. */
  const accountReady = !talent
    ? true
    : validEmail(email.trim()) && password.length >= 8;

  const patchDraft = (i, patch) =>
    setDrafts((list) => list.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  // Money is published, not just collected: it goes into the description so
  // the other side knows the number before they express interest. Posting has
  // no budget/rate column, so the description is where it can live and still
  // be seen on the board and in syndicated listings.
  const payloadFor = (d) => {
    const picked = DURATIONS.find((x) => x.value === duration);
    const body = (d.description || "").trim();
    const money = budget.trim();
    const line = money ? `${c.moneyPrefix}: ${money}` : "";
    return {
      poster_type: c.posterType,
      title: d.title.trim().slice(0, 255),
      description: line ? (body ? `${body}\n\n${line}` : line) : body,
      vertical: d.vertical || "",
      offering: d.offering || "",
      location: location.trim().slice(0, 300),
      available_from: startDate || "",
      available_until: addDays(startDate, picked?.days ?? null),
    };
  };

  /* --- step 1a (businesses): a job title -> a drafted posting --------------

     The same endpoint the old composer used, fed two words instead of a
     paragraph. It drafts a title and description in the buyer's voice, and the
     next screen is an editor, so nothing here is final — which is the point of
     asking for so little. If drafting is off or fails, what they typed becomes
     the title and they edit from there. Nothing in the UI distinguishes the
     two, so there's no "the AI failed" moment. */
  const draftFromPosition = async () => {
    const raw = positionTitle.trim();

    // An empty box is not a mistake to correct, it's an answer: they don't
    // want to post right now. Sending them on beats telling them off — the
    // predecessor's refusal is what produced 31 dead taps in a minute.
    if (raw.length < 3) return goToAccountOnly("no_position");

    setDrafting(true);
    trackEvent("need_draft_requested", {
      account_type: c.accountType, flow: "need_first", source: "position_title",
    });
    try {
      const res = await fetch(apiUrl("/api/accounts/public/need/suggest-posting/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          need: raw.slice(0, 200),
          poster_type: c.posterType,
          anonymous_id: getAnonymousId(),
        }),
        credentials: "omit",
        mode: "cors",
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (data?.available && data?.draft?.title) {
        setDrafts([{ ...blankDraft(), ...data.draft }]);
        if (data.draft.location && !location) setLocation(data.draft.location);
        /* The server now answers a model failure with a posting written from
           their own words rather than with nothing, so "ready" is no longer the
           same thing as "the model wrote it". Record which one arrived: the
           fallback is a floor, not a success, and a rising share of them is the
           signal that drafting is degraded. Without this the two are
           indistinguishable in the funnel, which is how a 6-of-16 success rate
           went three weeks without anyone noticing. */
        trackEvent("need_draft_ready", {
          account_type: c.accountType, flow: "need_first", source: "position_title", count: 1,
          drafted_by: data.source || "model",
        });
      }
      if (QUOTA_REASONS.has(data?.reason)) {
        // Out of drafts is worth saying out loud — otherwise the drop from a
        // written draft to a mechanical one looks like the help quietly
        // getting worse for no reason.
        setResumeNote(QUOTA_HELP);
        trackEvent("draft_quota_blocked", {
          account_type: c.accountType, flow: "need_first",
          source: "position_title", reason: data.reason,
        });
      }
      if (!data?.available || !data?.draft?.title) {
        /* Stays silent to the visitor, deliberately: their words are still on
           the screen and there is nothing they can do about our model. Silent
           to US was the mistake -- 10 of these left no trace at all. */
        trackEvent("need_draft_failed", {
          account_type: c.accountType, flow: "need_first",
          source: "position_title", reason: data?.reason || (res.ok ? "empty_response" : `http_${res.status}`),
        });
        setDrafts([{ ...blankDraft(), title: raw.slice(0, 255) }]);
      }
    } catch (err) {
      trackEvent("need_draft_failed", {
        account_type: c.accountType, flow: "need_first",
        source: "position_title", reason: "network",
      });
      setDrafts([{ ...blankDraft(), title: raw.slice(0, 255) }]);
    } finally {
      setDrafting(false);
      // From CompanyAsk the post goes straight to the 1-of-2 card.
      setStage(ask ? "ready" : "draft");
    }
  };

  // From CompanyAsk with a hire: draft from their words on arrival.
  const askDrafted = useRef(false);
  useEffect(() => {
    if (!ask || startAt || askDrafted.current) return;
    askDrafted.current = true;
    draftFromPosition();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* --- step 1b (individuals): a resume, read while they carry on -----------

     Nobody waits for this any more.

     Reading a resume properly — three postings and a whole profile, from a
     document — is a model call measured in tens of seconds. The version of
     this that awaited it inline made the first thing a person ever did on
     Sift be sitting still watching a progress bar, and one production upload
     sat there until Azure gave up at four minutes. Handing us a resume is the
     most generous thing anyone does in this funnel and we were charging a
     minute of dead time for it.

     None of that work needs them present. So the slow read is started and
     left running in the browser (see lib/resumeIntake) while they go straight
     to the account form; it finishes somewhere in the middle of them typing
     an email, and the finish-setup screen after login picks up the result.

     What they DO wait for is the fast half: text out of the file, name and
     city off the top of it, no model involved. Two seconds, hard capped, and
     if it misses the cap we simply move on with an empty name field.

     The file is not uploaded to us here. It's parsed in memory by an anonymous
     endpoint and kept in browser state, then attached to the profile for real
     once the account exists. Nothing of theirs is stored before they say yes. */
  /* Take what the fast read found, whenever it turns up.

     Runs twice for the same request: once inline if it answered quickly, and
     once when it eventually lands. Both are safe. It never overwrites anything
     already in a field, so a late answer cannot clobber something the person
     typed in the meantime, and the tracking event is fired once, on the inline
     path or the late one, whichever got there first. */
  const quickReadSeen = useRef(false);
  const fillFromQuickRead = (quick, { late }) => {
    if (!quick) return;
    if (quickReadSeen.current) return;
    quickReadSeen.current = true;
    setName((current) => (current.trim() ? current : quick.name || ""));
    setLocation((current) => (current.trim() ? current : quick.location || ""));
    trackEvent("resume_quick_read", {
      account_type: c.accountType, flow: "need_first", late,
      got_name: !!quick.name, got_location: !!quick.location,
    });
  };

  const onResumePicked = async (file) => {
    if (!file) return;
    setResumeFile(file);
    setResumeNote("");
    setResumeFailure(null);
    setDrafting(true);
    trackEvent("need_draft_requested", { account_type: c.accountType, flow: "need_first", source: "resume" });

    const anonymousId = getAnonymousId();

    // Started, deliberately not awaited. This outlives both this screen and
    // the navigation away from it.
    startResumeIntake(file, { anonymousId });

    /* Wait for the fast read, but only for a moment, and never cancel it.

       Production round-trips this in about two seconds once the CORS preflight
       is counted, which is right on the edge of what is worth pausing for. So
       the pause is short and the request is left running: if the name lands
       after we have moved on, `fillFromQuickRead` drops it into the form
       behind them. The earlier version aborted at the cap and threw away
       answers that were about to arrive. */
    const reading = quickReadResume(file, { anonymousId });
    reading.then((late) => fillFromQuickRead(late, { late: true }));

    const quick = await withinMs(reading, 1500);
    setDrafting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";

    /* A file we cannot open at all is worth stopping for, and only that.

       This is the one class of failure where the person has everything they
       need and the fix is thirty seconds in the app the file is already open
       in — telling them now, while the picker is right there, is worth the
       interruption. Everything else (a blurry photo, a resume too thin to
       draft from, our own drafting budget) is decided by the slow call that is
       still running, and interrupting signup to report it would trade a
       certain account for an uncertain post. Those surface after login, where
       there is nothing left to lose by mentioning them. */
    if (quick && quick.ok === false && !QUOTA_REASONS.has(quick.reason)) {
      clearResumeIntake();
      setResumeFile(null);
      const reason = quick.reason || "could_not_read";
      setResumeFailure({
        reason,
        help: RESUME_HELP[reason] || RESUME_HELP.could_not_read,
        tips: RESUME_RETRY_TIPS[reason] || [],
        salvaged: false,
        retryable: !RESUME_RETRY_WONT_HELP.has(reason),
      });
      trackEvent("resume_draft_failed", {
        account_type: c.accountType, flow: "need_first", reason, salvaged: false, stage: "quick_read",
      });
      return;
    }

    fillFromQuickRead(quick, { late: false });
    goToAccountOnly("resume");
  };

  /* Straight to the account, nothing posted.

     The only exit from the start screen other than a resume, and where a
     business begins. `source` distinguishes why they took it -- no resume to
     hand, or a file we couldn't read -- because those are different problems
     and only one of them is ours to fix. */
  /* When the account screen last appeared, and whether that still matters.

     Read by createAccountAndPost to swallow the second half of a double-tap:
     the start screen's skip button and this screen's submit button carry the
     same words and land within a few dozen pixels of each other, so tapping
     skip renders the form under a finger that is still coming down.

     Zeroed the instant they touch any field, which is the part that was
     missing. A flat 700ms window on the button rejects a REAL submit from
     anyone quick — autofill plus a fast tap clears it easily — and it does so
     in silence, which is the exact failure the guard was written to prevent,
     just pointed at a different victim. It cost three of the end-to-end tests
     below before it cost a person. Typing proves somebody is present and
     deliberate, so nothing after the first keystroke needs protecting. */
  const [accountArmedAt, setAccountArmedAt] = useState(0);
  useEffect(() => {
    setAccountArmedAt(stage === "account" ? Date.now() : 0);
  }, [stage]);
  const disarm = () => setAccountArmedAt(0);

  /* Put the new screen's top under their eyes, not the old screen's scroll
     offset.

     Swapping a stage replaces the content inside a container the browser is
     still scrolled halfway down. Someone who scrolled to reach the button at
     the bottom of the start screen gets the account screen at that same offset
     — which lands them below the fields, looking at the submit button, with
     the name and email boxes off-screen above. From there the only thing to
     press is the thing they just pressed, so they press it, and get told the
     email is empty. `autoFocus` on the first field was supposed to cover this
     and does not: the in-app webview that four fifths of this traffic uses
     ignores it.

     Instant, not smooth. A stage swap is not a place to animate, and a scroll
     still travelling is a scroll they can tap through. */
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch {
      /* older webviews: no options object, and no smooth to suppress anyway */
      window.scrollTo(0, 0);
    }
  }, [stage]);

  const goToAccountOnly = (source) => {
    setSkipPost(true);
    setResumeFailure(null);
    setStage("account");
    /* Arriving here off a resume is not skipping anything — they gave us the
       most useful thing in the funnel and the reading is still running. Firing
       `skip_post` for it would fold the best outcome on this side into the
       metric that counts the worst one. */
    if (source === "resume") return;
    gaTrack("get_started_skip_post", { account_type: c.accountType });
    trackEvent("get_started_skip_post", {
      account_type: c.accountType, flow: "need_first", audience, source,
    });
  };

  const toDetails = () => {
    if (!selected.length) {
      return toast.error(multi ? "Pick at least one post to continue." : "Give the post a title.");
    }
    return setStage("details");
  };

  const toReady = () => {
    trackEvent("need_details_completed", { account_type: c.accountType, flow: "need_first", posts: selected.length });
    setStage("ready");
  };

  /* Hand a freshly authenticated individual to the finish-setup screen.

     Signup's job ends at the account. Everything that used to sit between the
     resume and the account — which post, from where, starting when, at what
     rate — now happens after login, on a screen where the drafted posts are
     already on the page and the questions are about work they can see.

     Two things have to survive the jump. The reading of the resume is still in
     flight; lib/resumeIntake owns that promise at module scope precisely so it
     outlives this component and the navigation. The file itself exists only in
     this tab's memory, so it goes to the profile now, while there is finally
     an account to attach it to.

     The upload is not awaited. The finish screen reads the in-flight result
     from memory; this POST only matters as the fallback after a page reload,
     and making someone watch a multi-megabyte upload would put back exactly
     the wait this whole change removes. */
  /* Long enough that a slow phone on a bad connection still gets through,
     short enough that nobody decides the button is broken. */
  const REGISTER_TIMEOUT_MS = 25000;

  /* Did the account get made anyway?

     A register request that times out or dies on the network has two very
     different meanings, and from the browser they look identical: either
     nothing happened, or the server created the account and the reply never
     arrived. Treating both as failure is how somebody ends up retrying an
     email address that already exists, being told so, and giving up on a
     signup that actually worked.

     They just typed a password, so there is a way to ask. One login attempt:
     if it succeeds the account was real and they carry on into the product
     without ever knowing anything went wrong. If it fails, this was a genuine
     failure and they get told plainly.

     Only for individuals -- the company side registers without a password, so
     there is nothing here to sign in with. */
  const recoverFromLostResponse = async () => {
    if (!talent) return false;
    const emailVal = readField("email", email);
    const passwordVal = readFieldRaw("password", password);
    if (!emailVal || !passwordVal) return false;
    try {
      const res = await fetch(apiUrl("/api/accounts/login/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: emailVal, password: passwordVal }),
        credentials: "omit",
        mode: "cors",
      });
      if (!res.ok) return false;
      const data = await res.json().catch(() => null);
      if (!data?.token) return false;
      storeAuthTokens(data);
      trackEvent("signup_recovered_after_timeout", {
        account_type: c.accountType, flow: "need_first",
      });
      return true;
    } catch {
      return false;
    }
  };

  const handOffAfterAuth = async (method) => {
    clearSignupProgress();
    // Signed up to send a message on a post: back to it first, message waiting.
    const backToPost = pendingPostReturn();
    if (backToPost) return navigate(backToPost);

    if (resumeFile) {
      const form = new FormData();
      form.append("resume", resumeFile);
      form.append("overwrite", "true");
      fetchWithAuth("/api/accounts/resume/", { method: "POST", body: form }).catch(() => {
        /* the finish screen can ask for it again */
      });
    }

    trackEvent("signup_handoff_setup", {
      account_type: c.accountType, flow: "need_first", method, with_resume: !!resumeFile,
    });

    // Cream-label portals run their own activation handshake and have their
    // own idea of where a new account belongs.
    if (branding?.branded) {
      let dest = "/dashboard/contractor";
      try { dest = await resolvePostLoginPath(branding); } catch { /* keep fallback */ }
      return navigate(dest);
    }
    return navigate("/welcome");
  };

  /* Apple / Google / Microsoft. The component stores the tokens and fires the
     signup analytics itself, so all that is left is the same hand-off the
     email path takes — one door, one destination. */
  const finishWithSocialAuth = async (data) => {
    if (!data?.is_new_user) toast.success("Welcome back.");
    await handOffAfterAuth("oauth");
  };

  /* A referral has to survive all the way to an attribution row or the partner
     does not get paid.

     Two ways in. A partner's link carries the code on the URL (partner_code /
     ref / referral_code, because partners paste all three shapes), which now
     survives the redirect from /auth/organization/register. A partner who told
     someone their code over the phone is covered by the field on the account
     step. Either way it is applied the instant the org exists, via
     /api/partners/attribution/apply/ — the passwordless register endpoint has
     no idea what a partner code is, so this cannot ride along with signup.

     Swallowed on failure. A bad or expired code is not a reason to interrupt
     someone who has just successfully signed up, and it can be applied by hand
     afterwards from the partner admin. */
  const applyPartnerCode = async (orgId) => {
    const code = (partnerCode || "").trim();
    if (!code || !orgId) return;
    try {
      await fetchWithAuth("/api/partners/attribution/apply/", {
        method: "POST",
        json: { organization_id: String(orgId), referral_code: code },
      });
      trackEvent("partner_attribution_applied", { account_type: c.accountType, flow: "need_first" });
    } catch {
      trackEvent("partner_attribution_failed", { account_type: c.accountType, flow: "need_first" });
    }
  };

  // --- final step: create the account, then save everything ----------------
  const createAccountAndPost = async () => {
    /* Swallow the second half of a double-tap.

       The start screen's skip button and this screen's submit button land
       within a few dozen pixels of each other, so tapping skip renders this
       screen under a finger that is still coming down and the second tap
       submits a form nobody has typed in yet. The clickstream is unambiguous:
       repeatedly, `get_started_skip_post` and `signup_validation_blocked` land
       in the SAME SECOND, and one visitor was told "Please tell us your name"
       one second after asking for the form.

       This only ever caught the accident. It does nothing for the deliberate
       second tap two to five seconds later from someone who believes the first
       one failed, which was the larger half of the same wreck and is fixed
       where it starts: the two buttons no longer say the same thing, and the
       screen no longer arrives scrolled past its own fields.

       Being told off for a tap you didn't mean to make is worse than the tap
       doing nothing, so this is silent. A real submit is never this fast. */
    if (accountArmedAt && Date.now() - accountArmedAt < 700) return undefined;

    /* Validate what is IN the fields, not what React thinks is in them.

       Autofill writes straight into the input element and the change event
       React listens for does not reliably follow, least of all in the in-app
       webview most of this traffic uses. When that happens the field visibly
       holds their email, state still holds "", and we tell them to enter a
       valid email while they are looking at one — with no way to satisfy it.
       readField prefers state and falls back to the DOM, so the value we judge
       is the value they can see. */
    // Take anything the browser filled before reading, so the values we judge
    // are the ones on screen even if React never heard the fill.
    adoptAutofill();
    const nameVal = readField("name", name);
    const emailVal = readField("email", email);
    const orgVal = talent ? "" : readField("orgName", orgName);

    // Adopt anything recovered from the DOM so the rest of the flow — the
    // request body, the "we sent a link to {email}" line — agrees with it.
    if (nameVal !== name) setName(nameVal);
    if (emailVal !== email) setEmail(emailVal);
    if (!talent && orgVal !== orgName) setOrgName(orgVal);

    /* Nothing typed yet is not a mistake, it is somebody who has not started.

       A visitor on 2026-08-14 reached this form 1.5s after landing, tapped
       submit 1.5s later, then five more times over the next minute. Every
       attempt recorded `email, value_length: 0`. An error they could not see
       just made them tap again. It was still happening on 2026-08-20: an
       untouched form, ever_focused false, five seconds in, refused.

       This check used to sit BELOW the email validation, which made it dead
       code for the exact case it was written for -- an empty string fails
       validEmail, so the refusal returned first and the guard was never
       reached. `signup_empty_submit` fired once in its entire life while
       `value_length: 0` refusals kept coming. Order matters: ask "have they
       started" before "is it correct".

       So put the cursor in the first missing thing and open the keyboard.
       That is what the greyed button promises, and it is unmistakable.
       Recorded under its own name because it is not a refusal. */
    const pwdForEmptyCheck = talent ? readFieldRaw("password", password) : "";
    if (talent && !nameVal && !emailVal && !pwdForEmptyCheck) {
      trackEvent("signup_empty_submit", {
        account_type: c.accountType, flow: "need_first", has_name: !!nameVal,
      });
      const first = document.getElementById("nf-name") || document.getElementById("nf-email");
      if (first) first.focus();
      return undefined;
    }

    /* A blank name is not a reason to refuse somebody an account.

       In the first 24 hours this block was instrumented it fired 13 times
       across 9 visitors, 8 of them on this field, and not one of those 9 ever
       came back and finished. That is a day of signups thrown away over a
       field we can fill in from the address they just typed — and it is the
       same mistake as refusing an account for having no post selected, made
       one field to the left.

       So: derive it, record that we did, and carry on. The email is the only
       thing registration actually needs, because it is the identity. */
    let resolvedName = nameVal;
    if (!resolvedName && validEmail(emailVal)) {
      resolvedName = nameFromEmail(emailVal);
      trackEvent("signup_name_derived", {
        account_type: c.accountType, flow: "need_first", got: !!resolvedName,
      });
    }
    if (!validEmail(emailVal)) return failValidation("email", "Please enter a valid email.", { skipped_post: skipPost });
    /* No company-name gate. Email is the only thing signup asks a company for;
       the server names the company from the email's domain (or their name)
       and the guided home asks them to confirm it with a reminder that does
       not dismiss. */
    // Read from the DOM as well, for the same reason the fields above are: a
    // password manager that fills the box without firing React's change event
    // would otherwise be told the box it just filled is empty.
    const passwordVal = talent ? readFieldRaw("password", password) : "";

    if (talent) {
      if (passwordVal !== password) setPassword(passwordVal);
      if (passwordVal.length < 8) {
        return failValidation("password", "Please pick a password of at least 8 characters.", {
          skipped_post: skipPost,
        });
      }
    }
    /* Having no post selected is not a reason to refuse somebody an account.

       This used to throw them back to the draft screen with "Pick at least one
       post to save" — turning the optional half of the flow into a gate on the
       part they actually came for. If there is nothing to post, we make the
       account and say nothing; the composer is one tap away from the dashboard
       afterwards. `postSkipped` is local because setSkipPost would not have
       landed before this same run reads it. */
    const postSkipped = skipPost || !selected.length;
    if (postSkipped && !skipPost) setSkipPost(true);

    resetErrors();
    setSubmitting(true);
    trackEvent("signup_submitted", {
      account_type: c.accountType, flow: "need_first",
      skipped_post: postSkipped,
      // Distinguishes "chose to skip" from "had nothing selected", so the
      // no-post case stays visible now that it no longer blocks.
      post_absent: postSkipped && !skipPost,
    });
    /* A company's post is saved as a draft: the account screen promises it is
       "never posted without your permission", and the guided home is where they
       give it, with a Publish button. Individuals keep publishing on save. */
    const payloads = postSkipped ? [] : selected.map((d) => (talent ? payloadFor(d) : { ...payloadFor(d), status: "draft" }));
    const recoveryPath = `${c.postingsPath}?draft=1&first=1`;
    try {
      /* A submit that never comes back is the worst state this screen has.

         There was no timeout here at all, so a request that hung left the
         button reading "Creating your account…" for as long as somebody was
         willing to look at it. On 2026-08-18 a real person spent roughly forty
         minutes between opening this form and having an account, then told the
         campaign manager he had been stuck. His clickstream is blank, so this
         is not proven to be what happened to him, but it is the one path on
         this screen that can silently take forty minutes, and it should not
         exist either way.

         The dangerous part is that a lost response does not mean a lost
         account. The server can create it and have the reply die on a phone
         network, and then the person is sitting in front of a spinner for
         something that already succeeded. So the timeout does not just give
         up: `recoverFromLostResponse` below asks whether the account exists. */
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), REGISTER_TIMEOUT_MS);
      let res;
      try {
        res = await fetch(apiUrl(c.registerPath), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            name: resolvedName,
            first_name: resolvedName,
            email: emailVal,
            ...(talent ? { password: passwordVal } : { org_name: orgVal, business_type: bizType }),
            ...getAttributionPayload(),
          }),
          credentials: "omit",
          mode: "cors",
          signal: ac.signal,
        });
      } finally {
        clearTimeout(timer);
      }

      if (res.status === 409) {
        // Nothing to park when there is no post, so don't promise to finish
        // one — that toast reads as a bug to someone who never wrote it. This
        // also guards stashPendingDraft below from being handed undefined.
        if (postSkipped) {
          toast("You already have an account. Sign in and you're set.", { icon: "👋" });
          return navigate("/auth/login");
        }
        // Their post is finished work. Park it so signing in doesn't lose it.
        stashPendingDraft(payloads[0]);
        toast("You already have an account. Sign in and we'll finish your post.", { icon: "👋" });
        return navigate(`/auth/login?next=${encodeURIComponent(`${c.postingsPath}?draft=1`)}`);
      }
      if (!res.ok) {
        /* A server rejection is the other way a signup dies, and it was the
           quiet one: a bare toast, on the surface where toasts are least
           likely to be seen, and no event at all. In the funnel it looked
           identical to walking away. Put the message on the field the server
           named, and record it — `signup_rejected_by_server` is deliberately a
           different name from the client-side block so the two causes never
           get averaged together again. */
        const d = await res.json().catch(() => ({}));
        const serverField = d.email ? "email" : d.password ? "password" : d.org_name ? "orgName" : "email";
        /* 429 gets its own words. The generic "could not create your account"
           is both untrue and unactionable for a rate limit, and it arrived as
           a toast — which on the phones this funnel runs on is frequently
           rendered off-screen behind the keyboard, so the button just looked
           dead. Say what happened, on a field, where it cannot be covered. */
        const message = res.status === 429
          ? "Too many attempts from your network. Please wait a few minutes and try again."
          : (d.email?.[0] || d.password?.[0] || d.org_name?.[0] || d.error
             || "Could not create your account. Please try again.");
        trackEvent("signup_rejected_by_server", {
          account_type: c.accountType, flow: "need_first",
          status: res.status, field: serverField, skipped_post: postSkipped,
        });
        // Always on a field, never only a toast: `serverField` falls back to
        // email precisely so there is no path left where a rejected signup
        // says nothing the person can actually see.
        showFieldError(serverField, message);
        return;
      }

      const data = await res.json();
      storeAuthTokens(data);
      // The account is the point of no return: from here the work is on the
      // server, and what's left in local storage is just their email address.
      clearSignupProgress();
      await applyPartnerCode(data?.user?.organization?.id);
      const audience = signupAudience(c.accountType);
      metaTrack("CompleteRegistration", audience);
      metaTrack("StartTrial", audience);
      const method = passwordVal ? "password" : "passwordless";
      gaTrack("sign_up", { method, account_type: c.accountType });
      trackEvent("account_created", { method, account_type: c.accountType, flow: "need_first" });

      // A company that signed up to send a message on a post goes back to it
      // first; setup is waiting on their dashboard (pages/postings/
      // PublicPostingDetailPage). Individuals take the same turn in
      // handOffAfterAuth.
      if (!talent) {
        const backToPost = pendingPostReturn();
        if (backToPost) return navigate(backToPost);
      }

      /* Individuals are done here, whatever they arrived with.

         Nothing is posted during signup on this side any more. The resume is
         still being read, the questions that used to sit in the middle of this
         flow (where, from when, how long, what rate) belong next to the posts
         they are about, and both of those live on the finish-setup screen. */
      if (talent) return handOffAfterAuth("password");

      // No post to save: the account (and, for a business, its org) is the
      // entire deliverable. Nothing that can partially fail, so go straight to
      // the success screen — which is also the only place this passwordless
      // account can pick a password.
      if (postSkipped) {
        if (branding?.branded) {
          let dest = talent ? "/dashboard/contractor" : "/company/dashboard";
          try { dest = await resolvePostLoginPath(branding); } catch { /* keep fallback */ }
          return navigate(dest);
        }
        setSavedCount(0);
        setPending(false);
        setStage("saved");
        return undefined;
      }

      // Org postings need an organization to hang off; contractor postings
      // hang off the caller, so there's nothing extra to resolve.
      const orgId = talent ? null : data?.user?.organization?.id;
      if (!talent && !orgId) {
        // Account exists but we can't attribute the post. Hand the draft to
        // the composer rather than dropping it or posting it to nothing.
        stashPendingDraft(payloads[0]);
        return navigate(recoveryPath);
      }

      /* Saved independently so a rejection on the third doesn't take the first
         two down with it. Partial success is a real outcome here.

         Sent together rather than one after another. These are three
         independent POSTs with nothing between them, and awaiting each in turn
         made the wait the sum of all three -- on the last screen of signup,
         with somebody holding the button. allSettled keeps every guarantee the
         loop had: order is preserved, one failure cannot reject the others,
         and a rejected save still lands as null in its own slot. */
      const settled = await Promise.allSettled(
        payloads.map((payload) =>
          fetchWithAuth("/api/accounts/postings/", {
            method: "POST",
            json: { ...payload, ...(talent ? {} : { organization_id: String(orgId) }) },
          })
        )
      );
      const results = settled.map((r) => (r.status === "fulfilled" ? r.value : null));
      const landed = results.filter(Boolean);

      if (!landed.length) {
        // Account is real, nothing posted. Send the draft to the composer.
        stashPendingDraft(payloads[0]);
        toast("Almost there. Give your post one last look and save it.", { icon: "📝" });
        return navigate(recoveryPath);
      }
      if (landed.length < payloads.length) {
        toast(`Saved ${landed.length} of ${payloads.length} posts. You can add the rest from your dashboard.`, { icon: "📝" });
      }

      landed.forEach(() => gaTrack("job_posting_created", { poster_type: c.posterType }));
      landed.forEach(() => metaTrackCustom("JobPosted", signupAudience(c.accountType)));
      gaTrack("first_posting_created", { poster_type: c.posterType });
      trackEvent("first_posting_created", { poster_type: c.posterType, account_type: c.accountType, flow: "need_first", count: landed.length });

      // Cream-label portals do their own activation handshake, so hand off
      // there immediately rather than showing the generic success screen.
      if (branding?.branded) {
        let dest = talent ? "/dashboard/contractor" : "/company/dashboard";
        try { dest = await resolvePostLoginPath(branding); } catch { /* keep fallback */ }
        return navigate(dest);
      }

      setSavedCount(landed.length);
      setPending(landed.some((p) => p?.moderation_status === "pending"));
      setStage("saved");
      return undefined;
    } catch (err) {
      /* Timed out or the network died mid-request. Before calling it a
         failure, find out whether the account exists anyway -- see
         recoverFromLostResponse. If it does, this was never a failure from
         their side and they should simply carry on. */
      const aborted = err?.name === "AbortError";
      trackEvent("signup_request_failed", {
        account_type: c.accountType, flow: "need_first", aborted,
      });
      if (await recoverFromLostResponse()) {
        clearSignupProgress();
        return handOffAfterAuth("password");
      }
      toast.error(
        aborted
          ? "That took too long to go through. Please check your connection and try again."
          : "Network error. Please try again."
      );
      return undefined;
    } finally {
      setSubmitting(false);
    }
  };

  const savePassword = async () => {
    if ((pwd || "").length < 8) return showFieldError("newPassword", "Password must be at least 8 characters.");
    setSettingPwd(true);
    try {
      await fetchWithAuth("/api/accounts/set-password/", { method: "POST", json: { password: pwd } });
      setPwdSet(true);
      setPwd("");
      trackEvent("password_set", { account_type: c.accountType, flow: "need_first" });
      toast.success("Password set. You can log in anytime.");
    } catch (e) {
      // On the field, where it stays until they type (WCAG 3.3.1).
      showFieldError("newPassword", e?.data?.password?.[0] || "Could not set password. Please try again.");
    } finally {
      setSettingPwd(false);
    }
    return undefined;
  };

  const inputClass =
    "w-full h-12 px-4 bg-surface text-ink placeholder-faint border border-line-control rounded-lg [color-scheme:light] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors";
  const areaClass =
    "w-full px-4 py-3 bg-surface text-ink placeholder-faint border border-line-control rounded-lg focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors";
  const label = "block text-sm font-medium text-secondary mb-1.5";

  /* Two steps for an individual, five for a business.
     The talent path no longer drafts, edits, details and reveals a post before
     the account — all of that moved after login — so showing five dots and
     filling two of them would advertise three steps that never arrive. */
  const STAGES = talent ? ["start", "account"] : ask ? ["start", "ready", "account"] : ["start", "draft", "details", "ready", "account"];
  const stageIndex = stage === "saved" ? STAGES.length : STAGES.indexOf(stage);

  /* Show three real posts before asking them to write one.

     242 tagged ad visitors reached this form and seven made an account; the
     landing pages deliver at the same rate as every other source, so the loss
     is here. Three worked examples answer the question the empty composer
     leaves open ("what am I supposed to put?") in about four seconds.

     Talent side only -- a business arriving here already sees TalentPeek on
     its own route -- and only on the composer's first stage, so it never
     interrupts someone mid-flow. A restored draft skips it: they've already
     seen the format, and re-teaching it would read as losing their work. */
  if (PEEK_ENABLED && talent && stage === "start" && !peeked && !restored) {
    return (
      <PeerPeek
        onContinue={(via) => {
          trackEvent("peer_peek_continue", { via, audience });
          markPeekSeen();
          setPeeked(true);
        }}
      />
    );
  }

  // The reveal. Breaks out of the card so the real dashboard shell fills the
  // screen behind the prompt: the same teaser the get-started flow shows,
  // earned here by finishing a post rather than handed out on arrival.
  if (stage === "ready") {
    return (
      <ReadyReveal
        talent={talent}
        body={c.readyBody}
        onContinueTracked={() => {
          // "Reached sign-up" in the dashboard funnel. The predecessor fired
          // this after account creation; here the account step is a distinct
          // screen, so it belongs at the moment they choose to go to it.
          gaTrack("get_started_preview_start", { account_type: c.accountType });
          trackEvent("get_started_start", { audience, account_type: c.accountType, flow: "need_first" });
        }}
        posts={selected.map((d) => d.title)}
        /* From CompanyAsk the details screen is skipped, so say only what we
           know rather than a row of "TBD". */
        meta={(ask
          ? [location, startDate, budget.trim()]
          : [location || "Location TBD", startDate || "Start TBD", DURATIONS.find((d) => d.value === duration)?.label, budget.trim()])
          .filter(Boolean)
          .join(" • ")}
        orgName={orgName.trim() || "Your Company"}
        draft={selected[0]}
        location={location}
        mirror={ask?.text || ""}
        onContinue={() => setStage("account")}
        onBack={() => (ask && onBackToAsk ? onBackToAsk() : setStage("details"))}
      />
    );
  }

  return (
    <Shell>
      {/* Was a bare row of dots, aria-hidden and unlabelled: it showed
          position without saying how much was left, which is the only thing
          anybody wants to know on a signup form. */}
      <StepProgress step={stageIndex + 1} total={STAGES.length} />

      <AnimatePresence mode="wait">
        <motion.div
          key={stage}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.25 }}
        >
          {stage === "start" && drafting && (
            talent ? <OpeningResume typed={!resumeFile} /> : <DraftingProgress kind="position" />
          )}

          {stage === "start" && !drafting && (
            <>
              <h1 className="font-display italic text-ink text-2xl sm:text-3xl">{oldStart ? OLD_START.title : c.startTitle}</h1>
              {(oldStart ? OLD_START.sub : c.startSub) && (
                <p className="mt-2 text-secondary text-sm">{oldStart ? OLD_START.sub : c.startSub}</p>
              )}

              {talent ? (
                <>
                  {/* The resume is the individual's whole first screen. It is
                      the one thing in this funnel people actually completed:
                      every visitor who got past the old first step did it by
                      uploading one, and none by typing. */}
                  <input
                    id="gnf-resume-file"
                    aria-label="Upload your resume"
                    ref={fileInputRef}
                    type="file"
                    // MIME types alongside the extensions on purpose. Nearly
                    // everyone here is on a phone inside Meta's in-app browser,
                    // where an extension-only accept list can leave the picker
                    // showing Files and nothing else. `image/*` is what makes
                    // the camera and camera roll appear, and a photo of a
                    // printed resume is the only version most people have with
                    // them — the vision path reads one in about 16 seconds.
                    accept={RESUME_ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                      try { window.sessionStorage.removeItem(PICKER_FLAG); } catch { /* noop */ }
                      setPickerLost(false);
                      onResumePicked(e.target.files?.[0]);
                    }}
                  />
                  <button
                    type="button"
                    aria-controls="gnf-resume-file"
                    onClick={() => {
                      // Written before the picker opens, so it survives the
                      // webview being torn down and read back on the next mount.
                      try { window.sessionStorage.setItem(PICKER_FLAG, String(Date.now())); } catch { /* noop */ }
                      fileInputRef.current?.click();
                    }}
                    disabled={drafting}
                    className="w-full mt-5 flex items-start gap-3 text-left p-4 rounded-xl border border-dashed border-line-strong bg-sunken hover:border-brand hover:bg-surface transition-all disabled:opacity-60"
                  >
                    <span className="h-10 w-10 rounded-xl bg-brand-tint text-brand grid place-items-center flex-shrink-0">
                      <Upload size={19} strokeWidth={1.8} aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-ink font-semibold text-sm">
                        {(resumeNote || resumeFailure) ? "Try a different file" : "Upload your resume"}
                      </span>
                      {/* Only the filename gets truncated — the pitch needs to
                          wrap and be read, not get cut off mid-sentence. */}
                      {resumeFile && !resumeNote && !resumeFailure ? (
                        <span className="block text-secondary text-xs mt-0.5 min-w-0 break-words">{resumeFile.name}</span>
                      ) : (
                        <>
                          <span className="block text-secondary text-xs mt-1 leading-relaxed">
                            {oldStart
                              ? "We'll write three posts you can publish and fill in your profile from it."
                              : "Get your profile filled in and your post written."}
                          </span>
                          <span className="block text-tertiary text-[0.6875rem] mt-1">
                            PDF, Word, or a photo of it, a picture of a printed copy works.
                          </span>
                        </>
                      )}
                    </span>
                  </button>

                  {/* The way through without a file.
                      Meta's in-app browsers lose the picker often enough that
                      the upload cannot be the only road: 53% of Facebook and
                      45% of Instagram visitors who tapped it never reached
                      /welcome, against 90% on a real browser. This is offered
                      to everyone and led with when we know the picker just
                      failed, or when we can see which browser they are in. */}
                  {/* Two equal ways in. The typed path used to be a small "no resume
                      handy?" line under the upload, an afterthought; Jake wants it
                      to be an option in its own right, because plenty of people
                      have no resume on the phone they're holding. */}
                  {oldStart ? (
                    /* The `resume_first` arm: the screen as it ran through September. */
                    <div className="mt-4">
                      {pickerLost && (
                        <p className="mb-2 text-xs leading-relaxed rounded-xl border border-line bg-sunken px-3 py-2 text-secondary">
                          <b className="text-ink">That upload did not come back.</b>{" "}
                          {inApp
                            ? "The in-app browser inside Facebook and Instagram often drops file uploads. Open this page in Safari or Chrome, or just type what you do below."
                            : "Type what you do instead and we will write it for you."}
                        </p>
                      )}
                      <label className={label} htmlFor="nf-position-talent">
                        {pickerLost || inApp ? "Or type what you do" : "No resume handy? Type what you do"}
                      </label>
                      <input
                        id="nf-position-talent"
                        aria-label="Type what you do"
                        value={positionTitle}
                        maxLength={120}
                        placeholder="CNA, electrician, office manager…"
                        onChange={(e) => setPositionTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); draftFromPosition(); }
                        }}
                        className={inputClass}
                      />
                      <button
                        type="button"
                        onClick={draftFromPosition}
                        disabled={drafting}
                        className={btnPrimary + " mt-3"}
                      >
                        Write my posts
                      </button>
                    </div>
                  ) : (
                  <>
                  {!resumeFile && !resumeFailure && (
                    <div className="flex items-center gap-3 my-4" aria-hidden="true">
                      <span className="h-px flex-1 bg-line" />
                      <span className="text-xs text-tertiary">or</span>
                      <span className="h-px flex-1 bg-line" />
                    </div>
                  )}
                  <div className="rounded-xl border border-line-strong bg-sunken p-4">
                    {pickerLost && (
                      <p className="mb-2 text-xs leading-relaxed rounded-xl border border-line bg-surface px-3 py-2 text-secondary">
                        <b className="text-ink">That upload did not come back.</b>{" "}
                        {inApp
                          ? "The in-app browser inside Facebook and Instagram often drops file uploads. Open this page in Safari or Chrome, or just tell us the work you want below."
                          : "Tell us the work you want instead and we will write it for you."}
                      </p>
                    )}
                    <label className="block text-ink font-semibold text-sm" htmlFor="nf-position-talent">
                      Tell us what type of work you want to do
                    </label>
                    <span className="block text-secondary text-xs mt-1 mb-2 leading-relaxed">
                      A sentence or two is plenty. The post is written for you.
                    </span>
                    <textarea
                      id="nf-position-talent"
                      rows={3}
                      value={positionTitle}
                      maxLength={300}
                      placeholder="e.g. CNA with 5 years in home health, looking for weekday shifts near Carmel"
                      onChange={(e) => setPositionTitle(e.target.value)}
                      className={inputClass + " resize-none"}
                    />
                  </div>
                  {/* Appears once there's something to continue with. Picking a
                      resume starts on its own, so the button only ever means
                      "write my post from what I typed". */}
                  {positionTitle.trim() && (
                    <button
                      type="button"
                      onClick={draftFromPosition}
                      disabled={drafting}
                      className={btnGold + " mt-4"}
                    >
                      Continue
                    </button>
                  )}
                  </>
                  )}
                </>
              ) : (
                <>
                  {/* Two words, on one line. The predecessor asked "what do you
                      need done?" over a four-row textarea, which reads as an
                      essay prompt; a single-line field labelled with a job
                      title reads as a question you already know the answer to.
                      Enter submits, because on a phone the keyboard's Go key is
                      right there and reaching for the button is friction. */}
                  <label className={label + " mt-5"} htmlFor="nf-position">Job title</label>
                  <input
                    id="nf-position"
                    autoFocus
                    value={positionTitle}
                    maxLength={120}
                    onChange={(e) => setPositionTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); draftFromPosition(); }
                    }}
                    placeholder={c.positionPlaceholder}
                    enterKeyHint="go"
                    className={inputClass}
                  />
                  <p className="mt-1.5 text-xs text-tertiary">
                    We'll write the post from this. You get to edit it before anything goes live.
                  </p>

                  <button
                    onClick={draftFromPosition}
                    disabled={drafting}
                    className={btnPrimary + " mt-5"}
                  >
                    {drafting ? "One moment…" : "Write my post"}
                  </button>
                </>
              )}

              {resumeNote && (
                <p className="mt-3 text-xs text-secondary leading-relaxed bg-brand-tint border border-line rounded-lg p-3">
                  {resumeNote}
                </p>
              )}

              {resumeFailure && (
                <ResumeFailure
                  failure={resumeFailure}
                  onRetry={() => {
                    trackEvent("resume_retry", {
                      account_type: c.accountType, flow: "need_first", reason: resumeFailure.reason,
                    });
                    fileInputRef.current?.click();
                  }}
                  onSkip={() => {
                    trackEvent("resume_retry_skipped", {
                      account_type: c.accountType, flow: "need_first", reason: resumeFailure.reason,
                    });
                    goToAccountOnly("resume_failed");
                  }}
                />
              )}

              {/* Two doors, and the word that makes them read as two doors.

                  Without it the upload box and the account button stack into
                  what looks like a sequence — pick a file, THEN press the
                  button — which is the one reading that leaves someone stuck
                  when they don't have a resume on the phone they're holding.
                  It disappears the moment a file is chosen, because by then
                  they have answered the question and "or" is offering them a
                  choice they've already made. */}


              {/* Beside the offer, not behind it. The old version of this was a
                  faint link under two other faint links, and 0 of 11 visitors
                  ever used it, so people who didn't want to post had nowhere to
                  go. Whatever they were going to post, they can post from the
                  dashboard in a minute. */}
              {/* It says "skip this" because it is not the button that makes the
                  account -- it is the button that asks for the form. Naming it
                  "Create my account", the same words as the submit button one
                  screen later, told people the account was already made, so when
                  the next screen appeared they read it as the tap not having
                  landed and pressed again. 17 of the last 34 taps on this button
                  were followed by a second "Create my account" click, and 19 of
                  the 20 visitors who got the empty-field telling-off that
                  follows never came back. */}
              {/* Hidden while the resume-failure panel is up, for the same
                  reason the "Or" divider above is: that panel carries its own
                  way out, word for word, so leaving this one here puts two
                  identical buttons on one screen and makes the reader choose
                  between them for no reason. The panel wins because it is where
                  they are already looking. */}
              {oldStart && !resumeFile && !resumeFailure && (
                <div className="flex items-center gap-3 mt-4" aria-hidden="true">
                  <span className="h-px flex-1 bg-line" />
                  <span className="text-xs text-tertiary">Or</span>
                  <span className="h-px flex-1 bg-line" />
                </div>
              )}
              {!resumeFailure && (
                <button
                  onClick={() => goToAccountOnly(talent ? "no_resume" : "no_position")}
                  className={oldStart ? btnPrimary + " mt-3" : talent ? btnGhost : btnSecondary + " mt-4"}
                >
                  {talent && !oldStart ? "Skip for now and create my account" : "Skip this and create my account"}
                </button>
              )}

              {/* Quiet, not a fork in the road. The old version of this was a
                  full screen with two big cards that stopped everyone to ask a
                  question the CTA had already answered; 8 of the first 20
                  visitors answered it by leaving. Here it's one line under the
                  primary action, for the minority who need it. */}
              <button onClick={switchSide} className={btnGhost}>
                {talent ? "Actually, I'm hiring someone" : "Actually, I'm looking for work"}
              </button>
            </>
          )}

          {stage === "draft" && (
            <>
              <h1 className="font-display italic text-ink text-2xl sm:text-3xl">
                {multi ? `Here are ${drafts.length} posts` : "Here's your post"}
              </h1>
              <p className="mt-2 text-secondary text-sm">
                {multi
                  ? "Pick the ones that fit. Post one, two, or all of them, and edit anything."
                  : "Change anything you like. Nothing is posted yet."}
              </p>

              {multi ? (
                <div className="mt-5 space-y-3">
                  {drafts.map((d, i) => (
                    <div
                      key={i}
                      className="rounded-xl border p-4 transition-colors"
                      style={{ borderColor: d.selected ? "var(--brand)" : "var(--border)" }}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={d.selected}
                          onChange={(e) => patchDraft(i, { selected: e.target.checked })}
                          className="mt-1 h-4 w-4 flex-shrink-0"
                          style={{ accentColor: "var(--brand)" }}
                        />
                        <span className="text-ink font-medium text-sm leading-snug">{d.title}</span>
                      </label>

                      {d.selected && (
                        <div className="mt-3 pl-7">
                          <label className={label} htmlFor={`nf-title-${i}`}>Title</label>
                          <input
                            id={`nf-title-${i}`}
                            value={d.title}
                            maxLength={255}
                            onChange={(e) => patchDraft(i, { title: e.target.value })}
                            className={inputClass}
                          />
                          <label className={label + " mt-3"} htmlFor={`nf-desc-${i}`}>Description</label>
                          <textarea
                            id={`nf-desc-${i}`}
                            rows={6}
                            value={d.description}
                            onChange={(e) => patchDraft(i, { description: e.target.value })}
                            placeholder={c.descPlaceholder}
                            className={areaClass}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <label className={label + " mt-5"} htmlFor="nf-title">Title</label>
                  <input
                    id="nf-title"
                    autoFocus
                    value={drafts[0]?.title || ""}
                    maxLength={255}
                    onChange={(e) => patchDraft(0, { title: e.target.value, selected: true })}
                    placeholder={c.titlePlaceholder}
                    className={inputClass}
                  />

                  <label className={label + " mt-4"} htmlFor="nf-desc">Description</label>
                  <textarea
                    id="nf-desc"
                    rows={7}
                    value={drafts[0]?.description || ""}
                    onChange={(e) => patchDraft(0, { description: e.target.value })}
                    placeholder={c.descPlaceholder}
                    className={areaClass}
                  />
                </>
              )}

              <button onClick={toDetails} className={btnPrimary + " mt-6"}>
                {selected.length > 1 ? `Continue with ${selected.length} posts` : "Looks good, continue"}
              </button>
              <button onClick={() => setStage("start")} className={btnGhost}>Start over</button>
            </>
          )}

          {stage === "details" && (
            <>
              <h1 className="font-display italic text-ink text-2xl sm:text-3xl">A few details</h1>
              <p className="mt-2 text-secondary text-sm">
                {selected.length > 1
                  ? "These apply to every post you picked. Skip anything you don't know yet."
                  : "These are what people filter on. Skip anything you don't know yet."}
              </p>

              <label className={label + " mt-5"} htmlFor="nf-loc">{c.whereLabel}</label>
              {/* Suggests as you type, same as the company composer. This side
                  matters more, not less: the matcher compares location with an
                  ILIKE, so a worker who writes "Indy" is invisible to every
                  company that wrote "Indianapolis". The audience-specific
                  placeholder is kept -- the two sides ask for the same thing in
                  different words. */}
              <LocationInput
                id="nf-loc"
                value={location}
                onChange={setLocation}
                placeholder={c.wherePlaceholder}
                className={inputClass}
                inputProps={{ autoFocus: true }}
              />

              <label className={label + " mt-4"} htmlFor="nf-start">{c.whenLabel}</label>
              <input
                id="nf-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputClass}
              />

              <label className={label + " mt-4"} htmlFor="nf-dur">{c.durationLabel}</label>
              <select id="nf-dur" value={duration} onChange={(e) => setDuration(e.target.value)} className={inputClass}>
                {DURATIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
              {startDate && (DURATIONS.find((d) => d.value === duration)?.days ?? null) !== null && (
                <p className="mt-1.5 text-xs text-tertiary">
                  We'll set an expected end date of {addDays(startDate, DURATIONS.find((d) => d.value === duration).days)}. You can change it anytime.
                </p>
              )}

              <label className={label + " mt-4"} htmlFor="nf-budget">
                {c.moneyLabel} <span className="text-tertiary font-normal">(optional)</span>
              </label>
              <input
                id="nf-budget"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder={c.moneyPlaceholder}
                className={inputClass}
              />
              <p className="mt-1.5 text-xs text-tertiary">
                {c.moneyHint}
              </p>

              <button onClick={toReady} className={btnPrimary + " mt-6"}>Continue</button>
              <button onClick={() => setStage("draft")} className={btnGhost}>
                {selected.length > 1 ? "Back to the posts" : "Back to the post"}
              </button>
            </>
          )}

          {stage === "account" && (
            <>
              {/* The name field is on THIS screen, so it is always empty when
                  the heading first renders — which made it read "Last step,
                  there" to every single person who got here. `firstName` still
                  earns its fallback on the success screen, where a name has
                  actually been entered; here there is nothing to greet yet. */}
              {/* "Last step" is only true when there were earlier ones. A
                  business opens directly on this screen, so calling it the last
                  step would be the first thing it got wrong. */}
              {!talent && <FoundingCard />}
              {talent && (
              <h1 className="font-display italic text-ink text-2xl sm:text-3xl">
                {resumeIncoming
                  /* They handed us a resume seconds ago and we are reading it
                     right now. Naming that is the difference between "another
                     form" and "the last thing between me and what I was
                     promised". */
                  ? (name.trim() ? `Nearly there, ${firstName}` : "Nearly there")
                  : skipPost
                    ? "Create your account"
                    : (name.trim() ? `Last step, ${firstName}` : "Last step")}
              </h1>
              )}
              {talent && (
              <p className="mt-2 text-secondary text-sm">
                {resumeIncoming ? c.accountBody : skipPost ? c.accountBodySkip : c.accountBody}
              </p>
              )}

              {/* Their first post, typed out while they fill this in. It used
                  to be a note saying we were reading the file; the post itself
                  was first seen after login. See ResumePostPreview. */}
              {resumeIncoming && (
                <ResumePostPreview name={name} fileName={resumeFile?.name} />
              )}

              {/* One tap instead of three fields, and it lands in exactly the
                  same place. Offered above the form rather than under it: the
                  people most likely to want it are the ones least likely to
                  scroll past a password box to find it. */}
              {/* Companies get one tap too now: the account step asks for nothing
                  but an email, so a work login is the shortest way through. */}
              {(
                <div className="mt-5">
                  <SocialAuthButtons
                    verb={talent ? "Sign up" : "Continue"}
                    accountType={c.accountType}
                    disabled={submitting}
                    onAuthed={finishWithSocialAuth}
                  />
                  <div className="flex items-center gap-3 mt-4" aria-hidden="true">
                    <span className="h-px flex-1 bg-line" />
                    <span className="text-xs text-tertiary">Or use your email</span>
                    <span className="h-px flex-1 bg-line" />
                  </div>
                </div>
              )}

              {talent && (
                <>
                  <label className={label + " mt-5"} htmlFor="nf-name">Your name</label>
                  <input
                    id="nf-name"
                    {...fieldProps("name")}
                    autoFocus
                    value={name}
                    onChange={(e) => { adoptAutofill(); setName(e.target.value); clearError("name"); disarm(); }}
                    placeholder="Your name"
                    autoComplete="name"
                    className={inputClass}
                  />
                  <FieldError field="name" errors={errors} />
                </>
              )}

              <label className={label + (talent ? " mt-4" : " mt-2")} htmlFor="nf-email">{c.emailLabel}</label>
              <input
                id="nf-email"
                {...fieldProps("email")}
                type="email"
                value={email}
                onChange={(e) => { adoptAutofill(); setEmail(e.target.value); clearError("email"); disarm(); }}
                placeholder={c.emailPlaceholder}
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                className={inputClass}
              />
              <FieldError field="email" errors={errors} />

              {/* A password, asked for here, plainly.

                  This flow used to make a passwordless account and then offer
                  the password afterwards on the success screen, under a line
                  that presented having none as a feature ("no password to pick
                  right now"). It is not a feature. It means the only way back
                  into the account is a claim email, so anyone who signs up on
                  a phone and opens Sift later on a laptop is locked out of
                  work they have already done, and the screen where they could
                  have fixed it is one they will never see again.

                  Businesses stay passwordless for now — this is the talent
                  funnel's fix and the register endpoint takes the field either
                  way. */}
              {talent && (
                <>
                  <label className={label + " mt-4"} htmlFor="nf-password">Password</label>
                  <input
                    id="nf-password"
                    {...fieldProps("password")}
                    type="password"
                    value={password}
                    onChange={(e) => { adoptAutofill(); setPassword(e.target.value); clearError("password"); disarm(); }}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    className={inputClass}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); if (!submitting) createAccountAndPost(); }
                    }}
                    enterKeyHint="go"
                  />
                  <FieldError field="password" errors={errors} />
                </>
              )}

              {/* The button comes straight after the last thing they must fill.

                  It used to sit below the partner-code line and the industry
                  select, which on an iPhone in Meta's browser put it at y=673
                  in a 664px viewport -- off the bottom of the screen. People
                  who had just chosen "skip this, just create my account" landed
                  on four fields and no visible way to finish: two of them left
                  without touching a single input. Everything optional now lives
                  under the button instead of in front of it. */}
              {/* Say what is happening, not "Saving". The account creation call is
                  the one slow moment in this flow, and a person who taps a
                  button and gets a vague word for several seconds assumes it
                  did not work and taps again. Name the thing being made. */}
              {/* Looks inactive until the form is ready, but stays tappable.

                 A genuinely `disabled` button is how people ended up here. It gives no
                 feedback at all: you press it, nothing happens, you press again, you leave.
                 That is the same dead end as the six refusals one visitor collected on
                 2026-08-14, just quieter. So the grey sets the expectation and the tap still
                 does something useful -- it puts the cursor in the first thing that is
                 missing and opens the keyboard.
              
                 The state is carried by the styling alone, deliberately:
                 `aria-disabled` was the first attempt and it undoes the whole
                 point. It announces the control as unavailable, and tooling
                 that honours it refuses to activate the button at all --
                 Playwright rejects a click on one outright. A button we want
                 people to press must not tell anybody it cannot be pressed.
                 What it does on press is the affordance, and everyone gets the
                 same one. */}
              <button
                onClick={createAccountAndPost}
                disabled={submitting}
                className={(talent ? (accountReady ? btnPrimary : btnPrimaryIdle) : btnGold) + " mt-6"}
              >
                {submitting
                  ? "Creating your account…"
                  : !talent
                    ? "Continue"
                  : resumeIncoming
                    ? "Finish my account"
                    : skipPost
                      ? "Create my account"
                      : selected.length > 1 ? `Save my ${selected.length} posts` : "Save my post"}
              </button>

              {/* Says what happens next, not what isn't going to happen. The
                  predecessor opened with "No card" — the only mention of
                  payment anywhere in the funnel, put there to reassure people
                  about a worry the page had just introduced.

                  While submitting it becomes a reassurance instead, because on
                  a phone on mobile data this request is the longest wait in
                  the flow and silence during it reads as failure. */}
              <p className="mt-2.5 text-center text-xs text-tertiary" aria-live="polite">
                {submitting
                  ? "One moment, this can take a few seconds."
                  : talent
                    ? "Nothing goes public until you say so."
                    : skipPost
                      ? "No card needed."
                      : "Your post is saved, never posted without your permission."}
              </p>

              {!talent && (
                <div className="mt-5 pt-4 border-t border-line">
                  {/* Industry. Below the button because signup does not depend
                      on it, but still on this screen because it grounds matching
                      from day one and 8 of the first 11 orgs set it -- moving it
                      to a settings page they may never open would quietly lose
                      that. Defaults to General, so leaving it alone is fine. */}
                  <label className={label} htmlFor="nf-biz">
                    What does your company do? <span className="text-tertiary font-normal">(helps us match you)</span>
                  </label>
                  <select id="nf-biz" value={bizType} onChange={(e) => setBizType(e.target.value)} className={inputClass}>
                    {bizTypes.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>

                  {/* Referral code. One quiet line unless it's in play: a
                      business with no partner should not have to read a field
                      that does not apply to it. */}
                  {showPartnerCode ? (
                    <>
                      <label className={label + " mt-4"} htmlFor="nf-partner">
                        Partner code <span className="text-tertiary font-normal">(optional)</span>
                      </label>
                      <input
                        id="nf-partner"
                        value={partnerCode}
                        onChange={(e) => setPartnerCode(e.target.value)}
                        placeholder="Partner code"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck="false"
                        className={inputClass}
                      />
                      <p className="mt-1.5 text-xs text-tertiary">
                        Credits the partner who referred you. Nothing changes for you.
                      </p>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowPartnerCode(true)}
                      className="mt-3 text-xs text-tertiary underline underline-offset-2"
                    >
                      Have a partner code?
                    </button>
                  )}
                </div>
              )}

              {/* Back goes wherever they actually came from. Someone who
                  skipped never saw the reveal, so sending them to "ready" would
                  show them a post they never wrote. */}
              <button
                onClick={() => {
                  if (ask && skipPost && onBackToAsk) return onBackToAsk();
                  if (skipPost) setSkipPost(false);
                  return setStage(skipPost ? "start" : "ready");
                }}
                className={btnGhost}
              >
                Back
              </button>

              <p className="mt-4 text-center text-xs text-faint">
                Already have an account?{" "}
                <button onClick={() => navigate("/auth/login")} className="text-brand font-medium">Sign in</button>
              </p>
            </>
          )}

          {stage === "saved" && (
            <>
              <div className="text-4xl mb-2 text-center">{skipPost ? "👋" : "🎉"}</div>
              <h1 className="font-display italic text-ink text-2xl text-center">
                {skipPost
                  ? `You're in, ${firstName}`
                  : savedCount > 1
                    ? (pending ? `Your ${savedCount} posts are saved` : `Your ${savedCount} posts are live`)
                    : (pending ? "Your post is saved" : "Your post is live")}
              </h1>
              <p className="mt-2 text-secondary text-sm text-center">
                {skipPost ? c.savedSkipBody : (pending ? c.savedPending : c.savedLive)}
              </p>

              {/* No verification box: the step is off
                  (EMAIL_VERIFICATION_ENABLED) and no link is sent, so this
                  said "we sent a link to <your address>" about mail that does
                  not exist. */}

              {/* Passwordless account: a password now means they can come back
                  on any device without depending on the claim email. */}
              <div className="mt-3 border border-line rounded-xl p-4">
                {pwdSet ? (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="h-6 w-6 rounded-full bg-brand text-white flex items-center justify-center text-xs">✓</span>
                    <span className="text-ink font-medium">Password set</span>
                    <span className="text-secondary">You can log in anytime.</span>
                  </div>
                ) : (
                  <>
                    <label htmlFor="gnf-password" className="block text-ink text-sm font-medium">Create a password</label>
                    <p className="text-secondary text-sm mt-0.5">So you can log back in on any device.</p>
                    <div className="flex gap-2 mt-3">
                      <input
                        id="gnf-password"
                        {...fieldProps("newPassword")}
                        type="password"
                        value={pwd}
                        onChange={(e) => { setPwd(e.target.value); clearError("newPassword"); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!settingPwd) savePassword(); } }}
                        placeholder="Min. 8 characters"
                        className={inputClass + " flex-1"}
                      />
                      <button
                        onClick={savePassword}
                        disabled={settingPwd}
                        className="px-4 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-50 whitespace-nowrap"
                      >
                        {settingPwd ? "Saving…" : "Set"}
                      </button>
                    </div>
                    <FieldError field="newPassword" errors={errors} />
                  </>
                )}
              </div>

              {/* A skipper skipped because they wanted to look around, so the
                  primary action is the looking, not an empty dashboard. */}
              {/* Came for clients (CompanyAsk): their storefront is how clients find them. */}
              {!talent && ask?.intent === "find_clients" ? (
                <>
                  <button onClick={() => navigate("/company/storefront")} className={btnPrimary + " mt-6"}>
                    Set up my storefront
                  </button>
                  <button onClick={() => navigate(c.postingsPath)} className={btnGhost}>
                    Go to my dashboard
                  </button>
                </>
              ) : skipPost ? (
                <>
                  <button onClick={() => navigate(c.browsePath)} className={btnPrimary + " mt-6"}>
                    {c.browseLabel}
                  </button>
                  <button onClick={() => navigate(c.postingsPath)} className={btnGhost}>
                    Go to my dashboard
                  </button>
                </>
              ) : (
                <button onClick={() => navigate(c.postingsPath)} className={btnPrimary + " mt-6"}>
                  Go to my dashboard
                </button>
              )}
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </Shell>
  );
}

/* The reveal: the actual dashboard shell, inert behind a prompt. Same preview
   the get-started teaser shows, a shell of the real site with placeholder
   data, not a mock built for this screen. Here it's earned: they wrote a post,
   and this is what saving it opens. */
/* What to show when a resume couldn't be read.

   The predecessor was a paragraph in a tinted box. Someone on a budget Android
   photographed a printed resume, the vision path spent eleven seconds and
   judged it too blurry, and they left three seconds after the message
   appeared. The words were fine — it named the problem and the fix — but it
   was prose, and the only thing on the screen that looked clickable was the
   same upload box that had just rejected them.

   So: name the fix in specifics they can act on, and put both ways forward
   under it as buttons. Retrying is the primary action for a file we could not
   read, because they already have what they need and are seconds from
   succeeding. When a different file cannot help (we read it, there just wasn't
   enough in it) the retry is dropped rather than dangled.

   Amber, not red: nothing is broken and nothing of theirs is lost. */
function ResumeFailure({ failure, onRetry, onSkip }) {
  const { help, tips, salvaged, retryable } = failure;
  return (
    <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4" role="alert" aria-live="polite">
      <p className="text-sm font-semibold text-amber-900">
        {retryable ? "We couldn't read that one" : "We read it, but there wasn't much to go on"}
      </p>
      <p className="mt-1 text-xs text-amber-900/90 leading-relaxed">{help}</p>

      {tips.length > 0 && (
        <ul className="mt-2.5 space-y-1.5">
          {tips.map((t) => (
            <li key={t} className="flex gap-2 text-xs text-amber-900/90 leading-relaxed">
              <span aria-hidden="true">•</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Their file is not lost, and saying so removes the fear that retrying
          costs them the work they already did. */}
      {salvaged && (
        <p className="mt-2.5 text-xs text-amber-900/80 leading-relaxed">
          We kept what we could read for your profile, so that part isn't lost either way.
        </p>
      )}

      <div className="mt-3.5 flex flex-col gap-2">
        {retryable && (
          <button
            type="button"
            onClick={onRetry}
            className="w-full h-11 rounded-pill bg-amber-900 text-white text-sm font-semibold hover:bg-amber-800 active:scale-[.99] transition-all"
          >
            Try another file
          </button>
        )}
        {/* There is no composer to fall back to any more, and "your resume
            didn't work, now write it out yourself" was always a worse offer
            than "carry on without it". They can post from the dashboard. */}
        <button
          type="button"
          onClick={onSkip}
          className="w-full h-10 text-amber-900 text-sm font-medium underline underline-offset-2"
        >
          Skip this and create my account
        </button>
      </div>
    </div>
  );
}

/* Is this a phone? Not a styling question here — it decides whether a GPU
   effect that can take the whole page down gets applied. See ReadyReveal. */
function useCompactViewport() {
  const query = "(max-width: 767px)";
  const [compact, setCompact] = useState(() => {
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const onChange = (e) => setCompact(e.matches);
    // Safari before 14 only has the deprecated addListener, and a good share
    // of this traffic is old phones inside an in-app browser.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return compact;
}

/* What a company gets, said once, on the screen before we ask for anything.
   Jake's list, in the order a company meets them. */
const INCLUDED = [
  "Talent matched to your post",
  "Contracts written and e-signed",
  "Credentials and documents, with expiration alerts",
  "Mobile timecards, one-click approval",
  "Contractor payments and client invoicing",
  "Messaging and reporting",
];

/* Gold is the "go" colour of the signup screens on both sides. */
/* The `resume_first` arm of the individual_start test (lib/experiment.js): the
   first screen's words as they ran through September. */
const OLD_START = {
  title: "Start with your resume",
  sub: "We'll turn it into posts businesses can find and fill in your profile. Don't have it on your phone? Skip it, you can add it any time after you sign up. No account needed to try.",
};

const btnGold = "w-full min-h-[52px] rounded-pill font-bold text-[1.0625rem] text-ink bg-[#C9A14C] hover:brightness-105 active:scale-[.99] transition-all disabled:opacity-60";

function useFitCount(draft, location, enabled) {
  /* "7 people on Sift fit this", from the same matcher and bar the public
     scan uses. Shown only when somebody fits: a count of zero would be the
     exact "nobody's here" message this screen exists to avoid. */
  const [fit, setFit] = useState(null);
  useEffect(() => {
    if (!enabled || !draft?.title) return undefined;
    let alive = true;
    fetch(apiUrl("/api/accounts/public/company/scan/"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ role: draft.title, description: draft.description || "", location: location || "" }),
      credentials: "omit",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.matched) setFit({ count: d.match_count, cards: d.cards || [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [enabled, draft?.title, draft?.description, location]);
  return fit;
}

/* Founding Organization terms, with their place in line. Hidden once the 100
   are gone: the offer stops existing, so the sentence about it does too. */
function ThreeMonthsCard() {
  return (
    <div className="rounded-2xl border border-[#C9A14C] bg-[#FAF3E2] p-4">
      <span className="font-semibold text-ink">Your subscription is waived for three months</span>
      <p className="mt-1.5 text-sm text-ink leading-relaxed">A 10% fee on contract payments. No card needed.</p>
    </div>
  );
}

function FoundingCard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(apiUrl("/api/accounts/public/founding-stats/"), { credentials: "omit" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setStats(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const left = Number(stats?.orgs_left);
  if (!stats || !Number.isFinite(left)) return null;
  // Once the 100 are taken, the offer is three months free (Jake, 2026-10-04).
  if (left <= 0) return <ThreeMonthsCard />;
  const spot = Math.min(100, 100 - left + 1);
  return (
    <div className="rounded-2xl border border-[#C9A14C] bg-[#FAF3E2] p-4">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-semibold text-ink">Founding Organization</span>
        <span className="font-mono text-[0.6875rem] tracking-[.16em] text-[#7A5C17]">SPOT {spot} OF 100</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#C9A14C]/25 my-2.5" aria-hidden="true">
        <div className="h-1.5 rounded-full bg-[#C9A14C]" style={{ width: `${spot}%` }} />
      </div>
      <p className="text-sm text-ink leading-relaxed">Your subscription is waived for good, with a 4% transaction fee.</p>
      <p className="mt-1.5 text-xs text-secondary leading-relaxed">No card needed.</p>
    </div>
  );
}

function ReadyReveal({ talent, body, posts, meta, orgName, draft, location, mirror = "", onContinue, onContinueTracked, onBack }) {
  const many = posts.length > 1;
  const fit = useFitCount(draft, location, !talent);

  /* No backdrop blur on a phone.

     This screen lays a fullscreen backdrop-filter over a fully rendered
     dashboard, which forces the compositor to keep a second full-viewport
     buffer and re-filter it every frame. On desktop that is free. On a
     low-memory Android webview it is one of the most reliable ways to get the
     renderer process killed — and when Meta's in-app browser loses a
     renderer it silently reloads the page.

     We caught exactly that: someone reached this screen with a finished post
     and the page reloaded under them fifteen seconds later. The teaser behind
     the card is the whole point of the reveal, so it stays; the effect that
     can end the session does not. An opaque scrim reads the same. */
  const compact = useCompactViewport();
  // A step in the flow, not a popup: no Escape. Focus and Tab stay on the card.
  const dialog = useDialog();

  return (
    <div style={{ position: "relative", minHeight: "100vh" }}>
      {/* Snapshot only, no clicks through into protected routes. */}
      <div style={{ pointerEvents: "none", userSelect: "none" }} aria-hidden="true">
        {talent ? <ContractorDashboardPreview /> : <CompanyDashboardPreview orgName={orgName} />}
      </div>

      <div
        {...dialog.props}
        aria-labelledby={undefined}
        aria-label={many ? "Your posts are ready" : "Your post is ready"}
        style={{
          position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24, overflowY: "auto",
          background: compact ? "rgba(20,18,14,.72)" : "rgba(20,18,14,.5)",
          ...(compact ? {} : { backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)" }),
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative w-full max-w-md bg-surface border border-line rounded-2xl shadow-sift-md p-7 text-center my-auto"
        >
          {/* From CompanyAsk: Back in the top-left corner like every signup
              screen, and the card answers what they came for (Jake, 2026-10-04). */}
          {mirror && (
            <button type="button" onClick={onBack}
              className="absolute top-4 left-4 inline-flex items-center gap-1 min-h-[36px] px-1 text-xs text-secondary hover:text-ink">
              <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
            </button>
          )}
          <span className="h-12 w-12 rounded-full bg-brand-tint text-brand grid place-items-center mx-auto">
            <Lock size={22} strokeWidth={2} aria-hidden="true" />
          </span>
          <h1 className="font-display italic text-ink text-2xl mt-4">
            {mirror && !many ? "Here's how Sift fills it" : many ? `Your ${posts.length} posts are ready` : "Your post is ready"}
          </h1>
          <p className="mt-2 text-secondary text-sm">{mirror ? "Your post is written. Add your company details to save it." : body}</p>

          <div className="mt-4 border border-line rounded-xl p-4 bg-sunken text-left">
            <div className="text-[0.625rem] font-semibold uppercase tracking-wide text-tertiary">
              {many ? `Your ${posts.length} posts` : "Your post"}
            </div>
            {posts.map((t, i) => (
              <div key={i} className="mt-1.5 text-ink font-medium text-sm">{t}</div>
            ))}
            <div className="mt-2 text-xs text-tertiary">{meta}</div>
          </div>

          {!talent && fit?.count > 0 && (
            <div className="mt-3 flex items-center gap-2.5 text-left">
              <span className="flex">
                {fit.cards.slice(0, 3).map((c, i) => (
                  <span key={i} className="h-8 w-8 rounded-full bg-brand text-white text-[0.6875rem] font-semibold grid place-items-center border-2 border-surface" style={{ marginLeft: i ? -8 : 0 }}>
                    {c.initials}
                  </span>
                ))}
              </span>
              <span className="text-sm font-semibold text-brand">
                {fit.count} {fit.count === 1 ? "person on Sift fits" : "people on Sift fit"} this
              </span>
            </div>
          )}

          {!talent && (
            <div className="mt-4 border border-line rounded-xl p-4 text-left">
              <div className="text-sm font-semibold text-ink">Everything is included</div>
              <ul className="mt-2 space-y-1.5">
                {INCLUDED.map((t) => (
                  <li key={t} className="flex gap-2 text-sm text-secondary">
                    <span className="text-brand font-bold" aria-hidden="true">✓</span>{t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button onClick={() => { onContinueTracked?.(); onContinue(); }} className={(talent ? btnPrimary : btnGold) + " mt-5"}>
            {talent ? (many ? "Add my details & save my posts" : "Add my details & save my post") : "Continue"}
          </button>
          {!mirror && <button onClick={onBack} className={btnGhost}>Edit the details</button>}
        </motion.div>
      </div>
    </div>
  );
}

/* Drafting takes real time — reading a resume and writing three posts is tens
   of seconds, not a spinner's worth. Almost all of this traffic is a phone
   inside Meta's in-app browser, where a disabled button that says "One moment…"
   is indistinguishable from a page that has died, and the clickstream shows
   people leaving rather than waiting.

   The bar is a time-based estimate, not real progress: we get one response at
   the end, so there is nothing truthful to measure against. What it has to do
   is prove the page is alive. It decays toward 95% and stops, so it can never
   sit at 100% while the request is still running, and the step labels are the
   actual stages of the request, so the words stay true even when the timing
   guess is off. */
const DRAFT_STEPS = {
  position: ["Reading the role", "Writing your post"],
};

/* The two seconds an individual now waits after picking a resume, and nothing
   more. The step list and the decaying bar above are for the business path,
   where one request really does take half a minute; here the whole point is
   that the long read is happening somewhere else. Anything with progress
   furniture on it would imply there is something to wait for. */
function OpeningResume({ typed = false }) {
  /* The talent branch said "Opening your resume" whatever route somebody took,
     so anyone who typed a job title and handed over no file at all was told we
     were reading a resume they had never sent. Small, and exactly the kind of
     thing that makes a person think they pressed the wrong button. */
  return (
    <div role="status" aria-live="polite" className="py-6 text-center">
      <span className="inline-block h-8 w-8 rounded-full border-2 border-line border-t-brand animate-spin" aria-hidden="true" />
      <p className="mt-4 text-ink text-sm font-medium">
        {typed ? "Writing your posts" : "Opening your resume"}
      </p>
      <p className="mt-1 text-secondary text-xs">One second.</p>
    </div>
  );
}

function DraftingProgress({ kind }) {
  const steps = DRAFT_STEPS[kind] || DRAFT_STEPS.position;
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - started) / 1000), 250);
    return () => clearInterval(id);
  }, []);

  const pct = Math.min(95, 100 * (1 - Math.exp(-elapsed / 14)));
  const step = Math.min(steps.length - 1, Math.floor(elapsed / 7));

  return (
    <div role="status" aria-live="polite" className="py-2">
      <h1 className="font-display italic text-ink text-2xl sm:text-3xl">One moment</h1>
      <p className="mt-2 text-secondary text-sm">
        {kind === "resume"
          ? "We're turning your resume into posts businesses can find."
          : "We're turning that into a post businesses can find."}
      </p>

      <div className="mt-6 h-1.5 w-full rounded-full bg-sunken overflow-hidden" aria-hidden="true">
        <div
          className="h-full rounded-full bg-brand transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="mt-5 space-y-2.5">
        {steps.map((label, i) => (
          <li key={label} className="flex items-center gap-3 text-sm">
            <span
              className="h-5 w-5 rounded-full flex-shrink-0 grid place-items-center text-[0.6875rem] transition-colors"
              style={{
                background: i <= step ? "var(--brand)" : "var(--sunken, #f2f2f2)",
                color: i <= step ? "#fff" : "var(--text-tertiary)",
              }}
              aria-hidden="true"
            >
              {i < step ? "✓" : i + 1}
            </span>
            <span className={i <= step ? "text-ink" : "text-tertiary"}>
              {label}
              {i === step ? "…" : ""}
            </span>
          </li>
        ))}
      </ul>

      {/* Only after it has actually been slow — saying it up front invites the
          wait rather than covering for it. */}
      {elapsed > 20 && (
        <p className="mt-5 text-xs text-tertiary">
          Still working. This can take a few more seconds, you don't need to do anything.
        </p>
      )}
    </div>
  );
}

const btnPrimary =
  "w-full h-12 bg-brand text-white rounded-pill font-medium hover:bg-brand-hover active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed";

/* Same shape and size, visibly not ready. Muted rather than hidden, because
   the primary action must stay exactly where the eye expects it. Styling only,
   with a still-live handler: see the button that uses it. */
const btnPrimaryIdle =
  "w-full h-12 bg-sunken text-tertiary border border-line-strong rounded-pill font-medium active:scale-95 transition-all";
/* A real button for the second choice, not a link.

   "Skip this, just create my account" used to be a ghost line sitting under
   two other ghost lines. Nobody used it: 0 of 11 visitors in a day, 1 person
   in the month before. An alternative nobody can see isn't an alternative, and
   the people who needed it were the ones tapping a dead button instead. Same
   height and shape as the primary so it reads as a choice, outlined so it
   still reads as the second one. */
const btnSecondary =
  "w-full h-12 bg-surface text-ink border border-line-strong rounded-pill font-medium hover:border-brand hover:text-brand active:scale-95 transition-all";
const btnGhost = "w-full h-11 mt-2 text-secondary text-sm font-medium hover:text-ink transition-colors";

function Shell({ children }) {
  return (
    /* The card is centred on a desktop and top-aligned with a deep bottom
       gutter on a phone, and the gutter is the whole point.

       Centred, the document is exactly as tall as the viewport, so there is
       nothing to scroll. Raise the keyboard and the submit button sits behind
       it with no way to bring it up: measured on an iPhone viewport, the button
       spans y=534-582 while the keyboard leaves about 524px usable. People
       could type a name, could not reach the button, and the ones who did
       reach it had skipped the field the keyboard was covering.

       The gutter has to be viewport-relative, not a fixed 160px: `min-h-screen`
       simply absorbs a fixed padding when the card is shorter than the screen,
       leaving the document exactly one viewport tall and nothing to scroll.
       Half a viewport is always more than the card has to spare, so the page
       is reliably taller than the screen and the button can always be brought
       up over the keyboard. */
    <div
      className="min-h-screen bg-bg flex items-start sm:items-center justify-center px-4 pb-[50vh] sm:py-12"
      /* pt-8 alone put the card under the status bar in the native app: this is
         the first screen after the splash, so nothing sits above it to push the
         content clear of the notch. The inset is added to the padding rather
         than replacing it, and resolves to 0px in a browser, so the web layout
         is unchanged. Same treatment as CompanyDoors. */
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 2rem)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-surface border border-line rounded-2xl shadow-sift-sm p-8"
      >
        <div className="flex justify-center mb-5">
          <SiftMark size={44} />
        </div>
        {children}
      </motion.div>
    </div>
  );
}
